import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search,
  RefreshCw,
  Loader2,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Package,
  CheckCircle,
  AlertTriangle,
  Database,
  Ruler,
  ShieldCheck,
  Box,
  Truck,
  Globe,
  Layers,
  Plus,
  Trash2,
  Save,
  CheckSquare,
  ExternalLink,
  TrendingUp,
  Lightbulb,
  X as XIcon,
  Copy,
  Eye,
  ShoppingCart,
  Target,
  Sparkles,
  Pencil,
  Info,
  PackageX,
  BarChart3,
  Download,
  Upload,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axiosInstance from '../utils/axiosInstance';
import config from '../config';
import ShippyLoader from '../components/ShippyLoader';
import { ShippySpinner } from '../components/ShippyLoader';

const getCountryFlag = (iso2) => {
  if (!iso2 || iso2.length !== 2) return '';
  const codePoints = iso2.toUpperCase().split('').map(char => 0x1F1E6 + char.charCodeAt(0) - 65);
  return String.fromCodePoint(...codePoints);
};

const MethodBadge = ({ method }) => {
  let label = method;
  let colorClass = 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  const isRow = method.includes('ROW');

  if (method.includes('weight')) {
    label = method.includes('zone') ? 'Zone Weight' : 'Weight';
    colorClass = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  } else if (method.includes('volumetric')) {
    label = method.includes('zone') ? 'Zone Vol.' : 'Vol.';
    colorClass = 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
  } else if (method.includes('coli')) {
    label = 'Per-Parcel';
    colorClass = 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300';
  } else if (method.includes('pallet')) {
    label = 'Pallet';
    colorClass = 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  } else if (method === 'unknown') {
    label = 'No Rate';
    colorClass = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  }

  const zoneMatch = method.match(/\((?!ROW)(.+)\)/);
  if (zoneMatch) label += ` (${zoneMatch[1]})`;
  if (isRow) label += ' (ROW)';

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${colorClass}`}>
      {label}
    </span>
  );
};

const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative z-50 w-full max-w-3xl overflow-hidden rounded-xl border border-border/70 bg-card shadow-2xl max-h-[90vh]">
        <div className="h-[3px] bg-gradient-to-r from-brand via-brand-2 to-brand/40" aria-hidden />
        <div className="px-6 py-5 max-h-[calc(90vh-3px)] overflow-y-auto">
          <div className="flex items-start justify-between mb-4 gap-4">
            <h2 className="text-lg font-semibold flex items-center gap-2 leading-tight">{title}</h2>
            <button onClick={onClose} className="rounded-md p-1 hover:bg-accent transition-colors -mt-0.5">
              <X className="h-4 w-4" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

const ODOO_BASE_URL = config.odooBaseUrl;
const SHOPIFY_STORE = 'lamptwist-live';

// dimension_source badge metadata. Keep one source-of-truth for label + colour
// + tooltip across the rows table, the filter dropdown and any future surface.
const DIM_SOURCE_BADGES = {
  shopify_fallback:        { label: 'Shop',   tone: 'green',  tooltip: 'Real per-variant box from the Shopify custom.packaging metafield (no Odoo box yet)' },
  variant_fallback:        { label: 'Var',    tone: 'blue',   tooltip: 'Sibling variant of the same product' },
  sc_fallback:             { label: 'SC',     tone: 'blue',   tooltip: 'Second Chance original (physical copy)' },
  product_type_brand_avg:  { label: 'Type',   tone: 'teal',   tooltip: 'Same brand + same product type average' },
  brand_tag_overlap_avg:   { label: 'Tag',    tone: 'teal',   tooltip: 'Same brand + ≥1 shared tag average' },
  product_type_global_avg: { label: 'GType',  tone: 'orange', tooltip: 'Same product type, any brand' },
  global_tag_overlap_avg:  { label: 'GTag',   tone: 'orange', tooltip: '≥1 shared tag, any brand' },
  brand_average:           { label: 'Brand',  tone: 'orange', tooltip: 'Brand average (any product in brand)' },
  global_average:          { label: 'Global', tone: 'red',    tooltip: 'Global average (last resort)' },
};
const DIM_SOURCE_TONE_BADGE = {
  green:  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  blue:   'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  teal:   'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  red:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};
const DIM_SOURCE_TONE_ROW = {
  green:  'bg-green-50/40 dark:bg-green-950/15',
  blue:   'bg-blue-50/40 dark:bg-blue-950/15',
  teal:   'bg-teal-50/40 dark:bg-teal-950/15',
  orange: 'bg-orange-50/40 dark:bg-orange-950/15',
  red:    'bg-red-50/40 dark:bg-red-950/15',
};

const StackingRulesTab = () => {
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rulesSearch, setRulesSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(null);

  // Checkbox selection for bulk delete
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Add Rule Modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addResults, setAddResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [selected, setSelected] = useState({}); // variant_id → true
  const [bulkUnitsPerBox, setBulkUnitsPerBox] = useState('2');
  const [bulkSaving, setBulkSaving] = useState(false);

  const fetchRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      const resp = await axiosInstance.get('api/stacking-rules/', { params: { search: rulesSearch, page_size: 200 } });
      setRules(resp.data.data || []);
    } catch { /* ignore */ }
    setRulesLoading(false);
    setCheckedIds(new Set());
  }, [rulesSearch]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  // Debounced product search for adding rules
  useEffect(() => {
    if (!addModalOpen) return;
    if (addSearch.length < 2) { setAddResults([]); return; }
    const timer = setTimeout(async () => {
      setAddSearching(true);
      try {
        const resp = await axiosInstance.get('api/stacking-rules/search/', { params: { q: addSearch } });
        setAddResults(resp.data.results || []);
      } catch { /* ignore */ }
      setAddSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [addSearch, addModalOpen]);

  const toggleSelect = (variantId) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[variantId]) delete next[variantId];
      else next[variantId] = true;
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allSelected = addResults.length > 0 && addResults.every(r => selected[r.shopify_variant_id]);
    if (allSelected) {
      setSelected(prev => {
        const next = { ...prev };
        addResults.forEach(r => delete next[r.shopify_variant_id]);
        return next;
      });
    } else {
      setSelected(prev => {
        const next = { ...prev };
        addResults.forEach(r => { next[r.shopify_variant_id] = true; });
        return next;
      });
    }
  };

  const selectedCount = Object.keys(selected).length;

  const handleBulkSave = async () => {
    const val = parseInt(bulkUnitsPerBox);
    if (isNaN(val) || val < 1) return;
    setBulkSaving(true);
    const variantIds = Object.keys(selected);
    for (const vid of variantIds) {
      try {
        await axiosInstance.post('api/stacking-rules/', {
          shopify_variant_id: vid,
          units_per_box: val,
        });
      } catch { /* ignore */ }
    }
    setBulkSaving(false);
    setAddModalOpen(false);
    setSelected({});
    setAddSearch('');
    setAddResults([]);
    fetchRules();
  };

  const openAddModal = () => {
    setAddModalOpen(true);
    setAddSearch('');
    setAddResults([]);
    setSelected({});
    setBulkUnitsPerBox('2');
  };

  const handleSaveInline = async (rule) => {
    const val = parseInt(editValue);
    if (isNaN(val) || val < 1) { setEditingId(null); return; }
    setSaving(rule.id);
    try {
      await axiosInstance.post('api/stacking-rules/', {
        shopify_variant_id: rule.shopify_variant_id,
        units_per_box: val,
        notes: rule.notes,
      });
      fetchRules();
    } catch { /* ignore */ }
    setEditingId(null);
    setSaving(null);
  };

  const handleDelete = async (rule) => {
    if (!confirm(`Remove stacking rule for "${rule.product_name}"?`)) return;
    try {
      await axiosInstance.delete(`api/stacking-rules/?id=${rule.id}`);
      fetchRules();
    } catch { /* ignore */ }
  };

  // Checkbox helpers
  const toggleCheck = (id) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleCheckAll = () => {
    if (checkedIds.size === rules.length) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(rules.map(r => r.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (checkedIds.size === 0) return;
    if (!confirm(`Delete ${checkedIds.size} stacking rule${checkedIds.size > 1 ? 's' : ''}?`)) return;
    setBulkDeleting(true);
    try {
      await axiosInstance.delete(`api/stacking-rules/?ids=${[...checkedIds].join(',')}`);
      fetchRules();
    } catch { /* ignore */ }
    setBulkDeleting(false);
  };

  const buildShopifyUrl = (rule) => {
    const productId = rule.shopify_product_id;
    const variantId = rule.shopify_variant_id;
    if (!productId || !variantId) return null;
    const cleanProductId = String(productId).replace('.0', '');
    return `https://admin.shopify.com/store/${SHOPIFY_STORE}/products/${cleanProductId}/variants/${variantId}`;
  };

  const buildOdooUrl = (rule) => {
    const productId = rule.odoo_product_id;
    if (!productId) return null;
    return `${ODOO_BASE_URL}/web#id=${productId}&model=product.product&view_type=form`;
  };

  return (
    <div>
      {/* Header with Add Button */}
      <div className="flex items-center justify-between mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter rules..."
            value={rulesSearch}
            onChange={(e) => setRulesSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-3">
          {checkedIds.size > 0 && (
            <Button variant="outline" size="sm" onClick={handleBulkDelete} disabled={bulkDeleting}
              className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-950/30">
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Delete {checkedIds.size}
            </Button>
          )}
          <span className="text-sm text-muted-foreground">{rules.length} rule{rules.length !== 1 ? 's' : ''}</span>
          <Button onClick={openAddModal}>
            <Plus className="h-4 w-4 mr-1.5" /> Add Stacking Rule
          </Button>
        </div>
      </div>

      {/* Rules Table */}
      {rulesLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : rules.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Layers className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No stacking rules defined yet.</p>
          <p className="text-xs mt-1">Click "Add Stacking Rule" above to create one.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={rules.length > 0 && checkedIds.size === rules.length}
                    onChange={toggleCheckAll}
                    className="h-4 w-4 rounded border-input accent-primary"
                  />
                </th>
                <th className="text-left px-3 py-2 font-medium">Product</th>
                <th className="text-left px-3 py-2 font-medium w-36">Dimensions</th>
                <th className="text-center px-3 py-2 font-medium w-28">Units/Box</th>
                <th className="text-left px-3 py-2 font-medium w-40">Notes</th>
                <th className="text-center px-3 py-2 font-medium w-20">Links</th>
                <th className="text-center px-3 py-2 font-medium w-12"></th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => {
                const shopifyUrl = buildShopifyUrl(rule);
                const odooUrl = buildOdooUrl(rule);
                return (
                  <tr key={rule.id} className={`border-b border-border last:border-0 hover:bg-muted/20 ${checkedIds.has(rule.id) ? 'bg-primary/5' : ''}`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={checkedIds.has(rule.id)}
                        onChange={() => toggleCheck(rule.id)}
                        className="h-4 w-4 rounded border-input accent-primary"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-medium text-sm">{rule.product_name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">
                        {rule.product_sku || '—'} &middot; {rule.shopify_variant_id}
                      </p>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">
                      {rule.dimensions
                        ? `${rule.dimensions.length}x${rule.dimensions.width}x${rule.dimensions.height} cm`
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {editingId === rule.id ? (
                        <input
                          type="number"
                          min="1"
                          className="w-16 text-center border rounded px-1 py-0.5 text-sm bg-background"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => handleSaveInline(rule)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSaveInline(rule); if (e.key === 'Escape') setEditingId(null); }}
                          autoFocus
                        />
                      ) : (
                        <button
                          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 font-bold text-sm hover:ring-2 ring-blue-300"
                          onClick={() => { setEditingId(rule.id); setEditValue(String(rule.units_per_box)); }}
                        >
                          {saving === rule.id ? <Loader2 className="h-3 w-3 animate-spin" /> : rule.units_per_box}
                        </button>
                      )}
                      {rule.box_length_cm && (
                        <div className="text-[10px] text-muted-foreground mt-0.5 whitespace-nowrap" title="Overbox: see docs/nested-packs.md">
                          in {rule.box_length_cm}x{rule.box_width_cm}x{rule.box_height_cm} box{rule.box_weight_kg ? `, ${rule.box_weight_kg} kg full` : ''}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[10rem]">{rule.notes || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {shopifyUrl && (
                          <a href={shopifyUrl} target="_blank" rel="noopener noreferrer"
                            className="rounded p-1 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600"
                            title="Open in Shopify">
                            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor"><path d="M15.34 3.65c-.07 0-.13.04-.14.11l-.44 2.72c-.3-.1-.65-.16-1.01-.16-1.63 0-2.27 1.02-2.53 1.82-.18-.86-.83-1.56-1.82-1.56-.06 0-.12 0-.19.01l.3-1.86c.01-.07-.03-.13-.1-.14L7.66 4.3c-.07-.01-.13.03-.14.1l-1.66 10.3h3.06l.59-3.68c.13.14.32.24.54.3-.01.13-.02.27-.02.41 0 2.42 1.35 4.27 3.32 4.27 2.79 0 4.65-2.71 4.65-6.45 0-3.08-1.63-5.6-4.66-5.6-.57 0-1.09.1-1.54.28l.08-.47c.01-.07-.03-.13-.1-.14l-1.73-.29-.01.01-.01-.01c-.07-.01-.13.03-.14.1l-.33 2.05c.24-.08.52-.13.82-.13 1 0 1.5.75 1.5 1.57 0 1.35-.76 2.73-1.78 2.73-.61 0-.94-.47-.94-1.11 0-.15.02-.31.05-.47l1.02-6.33c.01-.07-.03-.13-.1-.14l-1.73-.29z"/></svg>
                          </a>
                        )}
                        {odooUrl && (
                          <a href={odooUrl} target="_blank" rel="noopener noreferrer"
                            className="rounded p-1 hover:bg-violet-100 dark:hover:bg-violet-900/30 text-violet-600"
                            title="Open in Odoo">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => handleDelete(rule)} className="text-muted-foreground hover:text-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Stacking Rule Modal */}
      <Modal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title={<><Layers className="h-5 w-5 text-primary" /> Add Stacking Rules</>}
      >
        <p className="text-sm text-muted-foreground mb-4">
          Search for products, select one or more variants, then set how many units fit per box.
        </p>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by product name or SKU..."
            value={addSearch}
            onChange={(e) => setAddSearch(e.target.value)}
            className="pl-9"
            autoFocus
          />
          {addSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
        </div>

        {/* Results with checkboxes */}
        {addResults.length > 0 && (
          <div className="rounded-lg border border-border max-h-72 overflow-y-auto mb-4">
            {/* Select all header */}
            <div className="sticky top-0 bg-muted/80 backdrop-blur-sm border-b border-border px-3 py-1.5 flex items-center gap-2">
              <input
                type="checkbox"
                checked={addResults.length > 0 && addResults.every(r => selected[r.shopify_variant_id])}
                onChange={toggleSelectAll}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              <span className="text-xs font-medium text-muted-foreground">
                Select all ({addResults.length} results)
              </span>
            </div>
            {addResults.map((r) => (
              <label
                key={r.shopify_variant_id}
                className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer border-b border-border last:border-0 transition-colors ${selected[r.shopify_variant_id] ? 'bg-primary/5' : 'hover:bg-muted/50'}`}
              >
                <input
                  type="checkbox"
                  checked={!!selected[r.shopify_variant_id]}
                  onChange={() => toggleSelect(r.shopify_variant_id)}
                  className="h-4 w-4 rounded border-input accent-primary flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{r.product_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.product_sku || 'No SKU'} &middot; {r.dimensions.length}x{r.dimensions.width}x{r.dimensions.height} cm &middot; {r.weight} kg
                  </p>
                </div>
                {r.current_units_per_box && (
                  <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded-full flex-shrink-0">
                    {r.current_units_per_box}/box
                  </span>
                )}
              </label>
            ))}
          </div>
        )}

        {addSearch.length >= 2 && !addSearching && addResults.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">No products found for "{addSearch}"</p>
        )}

        {/* Bottom: units per box + save */}
        {selectedCount > 0 && (
          <div className="flex items-center gap-3 pt-3 border-t border-border">
            <div className="flex items-center gap-2">
              <CheckSquare className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">{selectedCount} selected</span>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <label className="text-sm text-muted-foreground">Units per box:</label>
              <input
                type="number"
                min="1"
                value={bulkUnitsPerBox}
                onChange={(e) => setBulkUnitsPerBox(e.target.value)}
                className="w-16 text-center border rounded px-2 py-1 text-sm bg-background"
              />
              <Button onClick={handleBulkSave} disabled={bulkSaving || !bulkUnitsPerBox || parseInt(bulkUnitsPerBox) < 1}>
                {bulkSaving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                {bulkSaving ? 'Saving...' : `Save ${selectedCount} Rule${selectedCount > 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};


// -----------------------------------------------------------------------------
// Upright Rules tab: products that ship UPRIGHT AS DELIVERED (UprightRule).
// Length x width is the pallet floor, the height stays vertical: the engine
// never rotates or lays them flat (Gubi table #195289: 2 euro pallets joined,
// 240x80, top screwed on vertically).
// -----------------------------------------------------------------------------
const UprightRulesTab = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editNote, setEditNote] = useState('');

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addResults, setAddResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [selected, setSelected] = useState({});
  const [addNote, setAddNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axiosInstance.get('api/upright-rules/', { params: { search } });
      setRules(resp.data.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  useEffect(() => {
    if (!addModalOpen) return;
    if (addSearch.length < 2) { setAddResults([]); return; }
    const timer = setTimeout(async () => {
      setAddSearching(true);
      try {
        const resp = await axiosInstance.get('api/upright-rules/search/', { params: { q: addSearch } });
        setAddResults(resp.data.results || []);
      } catch { /* ignore */ }
      setAddSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [addSearch, addModalOpen]);

  const selectedCount = Object.keys(selected).length;

  const handleAdd = async () => {
    setSaving(true);
    for (const vid of Object.keys(selected)) {
      try {
        await axiosInstance.post('api/upright-rules/', { shopify_variant_id: vid, notes: addNote });
      } catch { /* ignore */ }
    }
    setSaving(false);
    setAddModalOpen(false);
    setSelected({});
    setAddSearch('');
    setAddResults([]);
    setAddNote('');
    fetchRules();
  };

  const handleSaveNote = async (rule) => {
    try {
      await axiosInstance.post('api/upright-rules/', { shopify_variant_id: rule.shopify_variant_id, notes: editNote });
      fetchRules();
    } catch { /* ignore */ }
    setEditingId(null);
  };

  const handleDelete = async (rule) => {
    if (!confirm(`Remove the upright rule for "${rule.product_name}"?`)) return;
    try {
      await axiosInstance.delete(`api/upright-rules/?id=${rule.id}`);
      fetchRules();
    } catch { /* ignore */ }
  };

  const dimsText = (d) => (d ? `${d.length}x${d.width}x${d.height} cm` : '—');

  return (
    <div>
      <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 mb-4 text-sm text-muted-foreground">
        A product listed here ships <span className="font-medium text-foreground">upright, as delivered</span>: its
        length &times; width is the pallet floor and its height stays vertical. Shippy never rotates it or lays it flat
        (for supplier-palletised pieces, e.g. a table top screwed vertically on two joined euro pallets). The 15 cm
        pallet deck is added on top as usual. Applies at checkout, on the B2B endpoint and in the simulator.
        Check the product&rsquo;s dimensions are entered as length &times; width = floor, height = standing height.
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Filter rules..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{rules.length} product{rules.length !== 1 ? 's' : ''}</span>
          <Button onClick={() => { setAddModalOpen(true); setAddSearch(''); setAddResults([]); setSelected({}); setAddNote(''); }}>
            <Plus className="h-4 w-4 mr-1.5" /> Add Upright Product
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : rules.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <ArrowUp className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No upright products yet.</p>
          <p className="text-xs mt-1">Click "Add Upright Product" above to add one.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left px-3 py-2 font-medium">Product</th>
                <th className="text-left px-3 py-2 font-medium w-40">L x W x H (floor, height)</th>
                <th className="text-left px-3 py-2 font-medium">Note</th>
                <th className="text-center px-3 py-2 font-medium w-12"></th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                  <td className="px-3 py-2">
                    <p className="font-medium text-sm">{rule.product_name || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground">{rule.product_sku || '—'} &middot; {rule.shopify_variant_id}</p>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono">{dimsText(rule.dimensions)}</td>
                  <td className="px-3 py-2 text-xs">
                    {editingId === rule.id ? (
                      <input
                        className="w-full border rounded px-2 py-1 text-xs bg-background"
                        value={editNote}
                        onChange={(e) => setEditNote(e.target.value)}
                        onBlur={() => handleSaveNote(rule)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveNote(rule); if (e.key === 'Escape') setEditingId(null); }}
                        autoFocus
                      />
                    ) : (
                      <button className="text-left text-muted-foreground hover:text-foreground w-full"
                        onClick={() => { setEditingId(rule.id); setEditNote(rule.notes || ''); }}>
                        {rule.notes || <span className="italic">add a note</span>}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => handleDelete(rule)} className="text-muted-foreground hover:text-red-500" title="Remove">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title={<><ArrowUp className="h-5 w-5 text-primary" /> Add Upright Products</>}
      >
        <p className="text-sm text-muted-foreground mb-4">
          Search for products, select the variants that ship upright as delivered, and add a short note (who delivers it, how).
        </p>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by product name or SKU..." value={addSearch} onChange={(e) => setAddSearch(e.target.value)} className="pl-9" autoFocus />
          {addSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        {addResults.length > 0 && (
          <div className="rounded-lg border border-border max-h-72 overflow-y-auto mb-4">
            {addResults.map((r) => (
              <label key={r.shopify_variant_id}
                className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer border-b border-border last:border-0 ${selected[r.shopify_variant_id] ? 'bg-primary/5' : 'hover:bg-muted/50'}`}>
                <input type="checkbox" checked={!!selected[r.shopify_variant_id]}
                  onChange={() => setSelected(prev => { const n = { ...prev }; if (n[r.shopify_variant_id]) delete n[r.shopify_variant_id]; else n[r.shopify_variant_id] = true; return n; })}
                  className="h-4 w-4 rounded border-input accent-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{r.product_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.product_sku || 'No SKU'} &middot; {dimsText(r.dimensions)} &middot; {r.weight} kg
                  </p>
                </div>
                {r.rule_id && (
                  <span className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 rounded-full flex-shrink-0">upright</span>
                )}
              </label>
            ))}
          </div>
        )}
        {addSearch.length >= 2 && !addSearching && addResults.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">No products found for "{addSearch}"</p>
        )}
        {selectedCount > 0 && (
          <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-border">
            <span className="text-sm font-medium">{selectedCount} selected</span>
            <Input className="flex-1 min-w-[12rem]" placeholder="Note, e.g. Gubi: 2 euro pallets, top screwed vertically"
              value={addNote} onChange={(e) => setAddNote(e.target.value)} />
            <Button onClick={handleAdd} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};


// -----------------------------------------------------------------------------
// Popular & Missing tab — GA4 popular products that still lack confirmed dims
// -----------------------------------------------------------------------------
const PopularMissingTab = () => {
  const PAGE_SIZE = 500;
  const [rows, setRows] = useState([]);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [period, setPeriod] = useState({ start: null, end: null, synced_at: null });
  const [proposalFor, setProposalFor] = useState(null);
  const [manualFor, setManualFor] = useState(null);
  const [inlineBusyId, setInlineBusyId] = useState(null);
  const [inlineDoneId, setInlineDoneId] = useState(null);

  const fetchPage = useCallback(async ({ offset = 0, append = false } = {}) => {
    if (append) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('api/packaging-data/popular-missing/', {
        params: { missing: onlyMissing ? 'true' : 'false', limit: PAGE_SIZE, offset },
      });
      const data = res.data.data || [];
      setRows(prev => append ? [...prev, ...data] : data);
      setTotalAvailable(res.data.total ?? data.length);
      setHasMore(!!res.data.has_more);
      setPeriod({
        start: res.data.period_start,
        end: res.data.period_end,
        synced_at: res.data.synced_at,
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [onlyMissing]);

  const fetchData = useCallback(() => fetchPage({ offset: 0, append: false }), [fetchPage]);
  const loadMore = useCallback(() => fetchPage({ offset: rows.length, append: true }), [fetchPage, rows.length]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Inline accept — apply best_sibling without opening the modal. Copies dims +
  // fragile/pallet flags only. box_confirmed is never touched here (physical
  // verification is a separate step in Boxopedia).
  const acceptBestSibling = useCallback(async (variant) => {
    if (!variant?.best_sibling) return;
    setInlineBusyId(variant.shopify_variant_id);
    setError(null);
    try {
      await axiosInstance.post('api/packaging-data/variant-proposals/', {
        target_variant_id: variant.shopify_variant_id,
        source_variant_id: variant.best_sibling.shopify_variant_id,
      });
      setInlineDoneId(variant.shopify_variant_id);
      setTimeout(() => {
        setInlineDoneId(null);
        fetchData();
      }, 900);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setInlineBusyId(null);
    }
  }, [fetchData]);

  // Group rows by product for rowspan-based rendering
  const groups = React.useMemo(() => {
    const out = [];
    rows.forEach(r => {
      const last = out[out.length - 1];
      if (last && last.shopify_product_id === r.shopify_product_id) {
        last.variants.push(r);
      } else {
        out.push({ ...r, variants: [r] });
      }
    });
    return out;
  }, [rows]);

  const noDataCount = rows.filter(r => !r.has_dims).length;
  const productCount = groups.length;

  const variantName = (v) => (v.variant_name || '').trim().replace(/^\/\s*|\s*\/$/g, '') || '—';
  const fmt = (n) => (n ?? 0).toLocaleString();

  return (
    <div className="space-y-6">
      {/* ========== Hero header ========== */}
      <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/5 via-card to-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 max-w-2xl">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-primary/10 p-2">
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>
              <h2 className="text-xl font-semibold">Popular variants · missing packaging data</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Variants of high-traffic products (GA4) that still need confirmed dimensions.
              Product metrics are totals across all variants; per-variant views come from GA4&apos;s <code className="text-[11px] bg-muted px-1 py-0.5 rounded">item_variant</code> dimension and may under-report where only English locale titles match our PIM.
            </p>
            {period.start && (
              <p className="text-[11px] text-muted-foreground/80 mt-2">
                Window <span className="font-medium">{period.start} → {period.end}</span>
                {period.synced_at && <> · Synced {new Date(period.synced_at).toLocaleString()}</>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none rounded-md border border-border bg-card px-2.5 py-1.5">
              <input type="checkbox" checked={onlyMissing}
                     onChange={(e) => setOnlyMissing(e.target.checked)}
                     className="h-3.5 w-3.5 rounded border-input accent-primary" />
              Only missing
            </label>
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <KpiPill icon={Package} label="Products" value={productCount} />
          <KpiPill icon={Target} label="Variants missing" value={rows.length} />
          <KpiPill icon={AlertTriangle} label="No dims at all" value={noDataCount} tone="red" />
          <KpiPill icon={Sparkles} label="Can inherit from sibling" value={rows.filter(r => r.has_dims && !r.box_confirmed).length} tone="amber" />
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      {/* ========== Product cards ========== */}
      {loading && rows.length === 0 ? (
        <div className="rounded-xl border border-border bg-card py-16 text-center text-sm text-muted-foreground">
          <RefreshCw className="h-6 w-6 mx-auto mb-3 animate-spin opacity-40" />
          Loading popular variants…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 py-16 text-center">
          <Package className="h-8 w-8 mx-auto mb-3 text-muted-foreground opacity-40" />
          <p className="text-sm text-muted-foreground">
            No data available. Run <code className="text-xs bg-muted px-1.5 py-0.5 rounded">python3 manage.py sync_popular_products</code> to populate.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map(g => (
            <article key={g.shopify_product_id}
                     className="rounded-xl border border-border bg-card overflow-hidden hover:shadow-sm transition-shadow">
              {/* ---------- Card header ---------- */}
              <header className="flex flex-wrap items-center gap-4 px-5 py-4 border-b border-border/60 bg-gradient-to-r from-muted/20 to-transparent">
                <div className="w-16 h-16 rounded-lg bg-muted/40 overflow-hidden flex items-center justify-center shrink-0 border border-border/60 shadow-sm">
                  {g.image ? (
                    <img src={g.image} alt="" loading="lazy"
                         className="w-full h-full object-cover"
                         onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <Package className="h-6 w-6 text-muted-foreground opacity-50" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold leading-snug truncate">
                    {g.product_name || '—'}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    {g.brand && <span className="text-xs text-muted-foreground">{g.brand}</span>}
                    <span className="text-[11px] text-muted-foreground/70">·</span>
                    <span className="text-[11px] text-muted-foreground/70">
                      {g.variants.length} of {g.total_variants} variant{g.total_variants !== 1 ? 's' : ''} missing
                    </span>
                  </div>
                </div>

                {/* Product-level metrics */}
                <div className="flex items-center gap-1.5">
                  <MetricChip icon={Eye} label="Views" value={fmt(g.product_views)} strong />
                  <MetricChip icon={ShoppingCart} label="Carts" value={fmt(g.product_add_to_carts)} />
                  <MetricChip icon={Target} label="Sales" value={fmt(g.product_purchases)} />
                </div>

                {/* Product links */}
                {g.shopify_product_id && (
                  <div className="flex items-center gap-1 ml-1">
                    <a href={`${ODOO_BASE_URL}/odoo/product.product?model=product.product&resId=${encodeURIComponent(`gid://shopify/Product/${g.shopify_product_id}`)}`}
                       target="_blank" rel="noopener noreferrer"
                       title="Open product in Odoo"
                       className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[11px] text-foreground/80 hover:bg-muted transition-colors">
                      Odoo <ExternalLink className="h-3 w-3" />
                    </a>
                    <a href={`https://admin.shopify.com/store/lamptwist/products/${g.shopify_product_id}`}
                       target="_blank" rel="noopener noreferrer"
                       title="Open product in Shopify admin"
                       className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[11px] text-foreground/80 hover:bg-muted transition-colors">
                      Shopify <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </header>

              {/* ---------- Variant list ---------- */}
              <ul className="divide-y divide-border/60">
                {g.variants.map(v => {
                  const canPropose = v.has_dims && !v.box_confirmed;
                  const pctViews = g.product_views > 0 ? Math.round((v.variant_views / g.product_views) * 100) : 0;
                  const bs = v.best_sibling;
                  const busy = inlineBusyId === v.shopify_variant_id;
                  const done = inlineDoneId === v.shopify_variant_id;
                  return (
                    <li key={v.shopify_variant_id}
                        className={`px-5 py-2.5 hover:bg-muted/30 transition-colors
                                    ${done ? 'bg-emerald-50/60 dark:bg-emerald-950/20' : ''}`}>
                      <div className="flex items-center gap-3">
                        {/* Variant name */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{variantName(v)}</p>
                          {v.sku && (
                            <p className="text-[10px] font-mono text-muted-foreground/70 truncate">{v.sku}</p>
                          )}
                        </div>

                        {/* Variant metrics */}
                        <div className="flex items-center gap-1.5 shrink-0">
                          <VariantMetric icon={Eye} value={v.variant_views} share={pctViews}
                                         tooltip={`${v.variant_views} variant views${pctViews > 0 ? ` (${pctViews}% of product)` : ''}`} />
                          <VariantMetric icon={ShoppingCart} value={v.variant_add_to_carts}
                                         tooltip={`${v.variant_add_to_carts} variant carts`} />
                          <VariantMetric icon={Target} value={v.variant_purchases}
                                         tooltip={`${v.variant_purchases} variant sales`} />
                        </div>

                        {/* Status */}
                        <StatusBadge v={v} />

                        {/* Actions */}
                        <div className="flex items-center gap-1 shrink-0">
                          {canPropose && (
                            <button type="button"
                                    onClick={() => setProposalFor(v)}
                                    className="inline-flex items-center gap-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20 px-2 py-1 text-[11px] font-medium transition-colors"
                                    title="See all sibling options">
                              <Lightbulb className="h-3 w-3" /> Propose
                            </button>
                          )}
                          {v.odoo_id && (
                            <button type="button"
                                    onClick={() => setManualFor(v)}
                                    className="inline-flex items-center gap-1 rounded-md border border-border text-foreground/80 hover:bg-muted px-2 py-1 text-[11px] font-medium transition-colors"
                                    title="Enter dimensions manually">
                              <Pencil className="h-3 w-3" /> Enter
                            </button>
                          )}
                          {v.odoo_id && (
                            <a href={`${ODOO_BASE_URL}/odoo/action-143/${v.odoo_id}`}
                               target="_blank" rel="noopener noreferrer"
                               title="Open variant in Odoo"
                               className="inline-flex items-center rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Inline best-sibling suggestion */}
                      {canPropose && bs && (
                        <div className="mt-1.5 flex items-center gap-2 rounded-md bg-muted/30 border border-border/60 px-2.5 py-1.5 text-[11px]">
                          <Sparkles className="h-3 w-3 text-primary shrink-0" />
                          <span className="text-muted-foreground shrink-0">Suggested from</span>
                          <span className="font-medium truncate">{bs.variant_title || bs.sku}</span>
                          {bs.boxes && bs.boxes.length > 1 ? (
                            <span className="font-mono text-muted-foreground whitespace-nowrap"
                                  title={bs.boxes.map((b, i) => `Box ${i+1}: ${b.length}×${b.width}×${b.height} cm, ${b.weight} kg`).join('\n')}>
                              {bs.boxes.length} boxes · {bs.total_weight} kg total
                            </span>
                          ) : bs.boxes && bs.boxes[0] ? (
                            <span className="font-mono text-muted-foreground whitespace-nowrap">
                              {bs.boxes[0].length}×{bs.boxes[0].width}×{bs.boxes[0].height} cm · {bs.boxes[0].weight} kg
                            </span>
                          ) : null}
                          <div className="flex-1" />
                          {done ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 font-medium">
                              <CheckCircle className="h-3 w-3" /> Applied
                            </span>
                          ) : (
                            <button type="button"
                                    onClick={() => acceptBestSibling(v)}
                                    disabled={busy}
                                    className="inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground hover:opacity-90 px-2 py-0.5 text-[11px] font-medium disabled:opacity-60 transition-opacity"
                                    title="Copy this sibling's dims, fragile and pallet flags to Odoo. box_confirmed is unchanged — confirm separately after physical check.">
                              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                              {busy ? 'Applying…' : 'Accept'}
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </article>
          ))}
        </div>
      )}

      {/* Pagination / load more */}
      {rows.length > 0 && hasMore && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <p className="text-xs text-muted-foreground">
            Showing <span className="font-semibold text-foreground">{rows.length}</span> of {totalAvailable.toLocaleString()} missing variants
          </p>
          <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ChevronDown className="h-3.5 w-3.5 mr-1.5" />}
            {loadingMore ? 'Loading…' : `Load next ${PAGE_SIZE}`}
          </Button>
        </div>
      )}
      {rows.length > 0 && !hasMore && totalAvailable > PAGE_SIZE && (
        <p className="text-[11px] text-center text-muted-foreground pt-1">
          All {totalAvailable.toLocaleString()} variants loaded.
        </p>
      )}

      {proposalFor && (
        <ProposalModal
          variant={proposalFor}
          onClose={() => setProposalFor(null)}
          onApplied={() => { setProposalFor(null); fetchData(); }}
        />
      )}
      {manualFor && (
        <ManualEntryModal
          variant={manualFor}
          onClose={() => setManualFor(null)}
          onSaved={() => { setManualFor(null); fetchData(); }}
        />
      )}
    </div>
  );
};

// ---------- Small presentation helpers ----------

const KpiPill = ({ icon: Icon, label, value, tone = 'default' }) => {
  const toneClass =
    tone === 'red' ? 'text-red-600 dark:text-red-400'
    : tone === 'amber' ? 'text-amber-600 dark:text-amber-400'
    : 'text-foreground';
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-3">
      <div className="rounded-lg bg-muted/40 p-2">
        <Icon className={`h-4 w-4 ${toneClass}`} />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className={`text-xl font-bold leading-tight ${toneClass}`}>{value}</p>
      </div>
    </div>
  );
};

const MetricChip = ({ icon: Icon, label, value, strong = false }) => (
  <div className="flex items-center gap-1.5 rounded-md border border-border/70 bg-card px-2.5 py-1 text-xs"
       title={label}>
    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
    <span className={`tabular-nums ${strong ? 'font-semibold' : 'font-medium text-foreground/90'}`}>{value}</span>
  </div>
);

const VariantMetric = ({ icon: Icon, value, share, tooltip }) => (
  <div className={`flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] tabular-nums
                   ${value > 0 ? 'text-foreground/80 bg-muted/40' : 'text-muted-foreground/40'}`}
       title={tooltip}>
    <Icon className="h-3 w-3" />
    <span className="min-w-[1.5rem] text-right">{value ?? 0}</span>
    {share > 0 && <span className="text-[9px] text-muted-foreground/70">{share}%</span>}
  </div>
);

const StatusBadge = ({ v }) => {
  if (!v.has_dims) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 text-[10px] font-medium whitespace-nowrap">
        <AlertTriangle className="h-2.5 w-2.5" /> No dims
      </span>
    );
  }
  if (v.box_confirmed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-medium whitespace-nowrap">
        <CheckCircle className="h-2.5 w-2.5" /> Confirmed
      </span>
    );
  }
  const label = v.dimension_source === 'odoo'
    ? 'Odoo, unconfirmed'
    : (v.dimension_source || 'fallback').replace(/_/g, ' ');
  return (
    <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-[10px] font-medium whitespace-nowrap capitalize">
      {label}
    </span>
  );
};


// -----------------------------------------------------------------------------
// Proposal modal — shows confirmed sibling variants to inherit dims/flags from
// -----------------------------------------------------------------------------
const ProposalModal = ({ variant, onClose, onApplied }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [confirmingId, setConfirmingId] = useState(null); // source_variant_id pending confirm
  const [applyingId, setApplyingId] = useState(null);
  const [successId, setSuccessId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axiosInstance.get('api/packaging-data/variant-proposals/', {
          params: { variant_id: variant.shopify_variant_id },
        });
        if (cancelled) return;
        setProposals(res.data.proposals || []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [variant.shopify_variant_id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { if (confirmingId) setConfirmingId(null); else onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, confirmingId]);

  const applyProposal = async (sourceVid) => {
    setApplyingId(sourceVid);
    setError(null);
    try {
      const res = await axiosInstance.post('api/packaging-data/variant-proposals/', {
        target_variant_id: variant.shopify_variant_id,
        source_variant_id: sourceVid,
      });
      setSuccessId(sourceVid);
      setConfirmingId(null);
      setTimeout(() => { onApplied?.(); }, 900);
      if (res.data.warning) setError(res.data.warning);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setApplyingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         onClick={onClose}>
      <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" /> Propose dimensions from a sibling variant
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Inherit confirmed packaging data from another variant of{' '}
              <span className="font-medium text-foreground">{variant.product_name}</span>.
              Target: <span className="font-mono">{variant.sku}</span>
              {variant.variant_name && <> — {variant.variant_name}</>}.
            </p>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 hover:bg-muted">
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground text-sm">Loading proposals…</div>
          ) : error ? (
            <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-800 dark:text-red-200">
              {error}
            </div>
          ) : proposals.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No confirmed sibling variants available for this product.
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                {proposals.length} sibling variant{proposals.length > 1 ? 's' : ''} with confirmed packaging data.
                Click <span className="font-medium text-foreground">Apply</span> to copy that sibling&apos;s dimensions and fragile &amp; pallet flags into this variant&apos;s Odoo record. <span className="font-mono">box_confirmed</span> stays off — confirm separately in Boxopedia after a physical check.
              </p>
              {proposals.map(p => {
                const isConfirming = confirmingId === p.shopify_variant_id;
                const isApplying = applyingId === p.shopify_variant_id;
                const isSuccess = successId === p.shopify_variant_id;
                return (
                  <div key={p.shopify_variant_id}
                       className={`rounded-lg border bg-background p-4 flex items-start gap-4 transition-all
                                   ${isSuccess ? 'border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-border'}`}>
                    <div className="w-14 h-14 rounded bg-muted/40 overflow-hidden flex items-center justify-center shrink-0 border border-border/60">
                      {p.image ? (
                        <img src={p.image} alt="" loading="lazy"
                             className="w-full h-full object-cover"
                             onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : (
                        <Package className="h-5 w-5 text-muted-foreground opacity-50" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">{p.variant_title || '—'}</p>
                        <span className="font-mono text-xs text-muted-foreground">{p.sku}</span>
                        {p.best_match && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-medium">
                            <Sparkles className="h-2.5 w-2.5" /> Best match
                          </span>
                        )}
                        {p.box_confirmed && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-medium">
                            <CheckCircle className="h-2.5 w-2.5" /> Confirmed
                          </span>
                        )}
                        {p.dimension_source === 'odoo' && !p.box_confirmed && (
                          <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-0.5 text-[10px] font-medium">
                            Odoo
                          </span>
                        )}
                      </div>
                      <div className="mt-2 text-xs space-y-1">
                        {(p.boxes || []).map((b, i) => (
                          <div key={i} className="flex items-center gap-2">
                            {p.boxes.length > 1 && (
                              <span className="inline-flex items-center rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                                Box {b.box_number || i + 1}
                              </span>
                            )}
                            <span className="font-mono">{b.length} × {b.width} × {b.height} cm</span>
                            <span className="text-muted-foreground">·</span>
                            <span className="font-mono">{b.weight} kg</span>
                          </div>
                        ))}
                        <div className="flex items-center gap-3 pt-1 text-muted-foreground">
                          {p.boxes && p.boxes.length > 1 && (
                            <span>Total: <span className="font-mono text-foreground">{p.total_weight} kg</span></span>
                          )}
                          {p.fragile && <span className="inline-flex items-center gap-1"><AlertTriangle className="h-2.5 w-2.5" /> Fragile</span>}
                          {p.shipped_on_pallet && <span className="inline-flex items-center gap-1"><Truck className="h-2.5 w-2.5" /> Pallet</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0 w-40">
                      {isSuccess ? (
                        <div className="inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-3 py-1.5 text-xs font-medium">
                          <CheckCircle className="h-3.5 w-3.5" /> Applied
                        </div>
                      ) : isConfirming ? (
                        <>
                          <Button size="sm" onClick={() => applyProposal(p.shopify_variant_id)}
                                  disabled={isApplying}
                                  className="gap-1.5 bg-primary text-primary-foreground">
                            {isApplying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
                            {isApplying ? 'Applying…' : 'Confirm apply'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setConfirmingId(null)}
                                  disabled={isApplying}>
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="outline"
                                  onClick={() => setConfirmingId(p.shopify_variant_id)}
                                  disabled={!!applyingId}
                                  className="gap-1.5">
                            <Copy className="h-3.5 w-3.5" /> Apply
                          </Button>
                          {p.odoo_id && (
                            <a href={`${ODOO_BASE_URL}/odoo/action-143/${p.odoo_id}`}
                               target="_blank" rel="noopener noreferrer"
                               className="text-[11px] text-primary hover:underline whitespace-nowrap text-center">
                              Open in Odoo <ExternalLink className="inline-block h-3 w-3 ml-0.5 -mt-0.5" />
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 flex items-center justify-between gap-3">
          <p className="text-[11px] text-muted-foreground">
            Apply writes to Odoo directly: clones <span className="font-mono">packaging.info</span> records and copies fragile / pallet flags.
            <span className="font-mono"> box_confirmed</span> is opt-in (verify first).
          </p>
          {variant.odoo_id && (
            <a href={`${ODOO_BASE_URL}/odoo/action-143/${variant.odoo_id}`}
               target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline whitespace-nowrap">
              Open target in Odoo <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};


// -----------------------------------------------------------------------------
// Manual-entry modal — user-typed dimensions, written straight to Odoo + local DB
// -----------------------------------------------------------------------------
const ManualEntryModal = ({ variant, onClose, onSaved }) => {
  const [boxes, setBoxes] = useState([{ length: '', width: '', height: '', weight: '' }]);
  const [fragile, setFragile] = useState(false);
  const [onPallet, setOnPallet] = useState(false);
  const [confirmBox, setConfirmBox] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const updateBox = (i, key, value) => {
    setBoxes(prev => prev.map((b, idx) => idx === i ? { ...b, [key]: value } : b));
  };
  const addBox = () => setBoxes(prev => [...prev, { length: '', width: '', height: '', weight: '' }]);
  const removeBox = (i) => setBoxes(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  const totalWeight = boxes.reduce((a, b) => a + (parseFloat(b.weight) || 0), 0);
  const totalVolWeight = boxes.reduce((a, b) => {
    const l = parseFloat(b.length), w = parseFloat(b.width), h = parseFloat(b.height);
    if (!l || !w || !h) return a;
    return a + (l * w * h) / 5000;
  }, 0);

  const canSubmit = boxes.every(b =>
    b.length && b.width && b.height && b.weight &&
    [b.length, b.width, b.height, b.weight].every(v => parseFloat(v) > 0)
  );

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await axiosInstance.post('api/packaging-data/variant-manual/', {
        variant_id: variant.shopify_variant_id,
        boxes: boxes.map(b => ({
          length: parseFloat(b.length),
          width: parseFloat(b.width),
          height: parseFloat(b.height),
          weight: parseFloat(b.weight),
        })),
        fragile,
        shipped_on_pallet: onPallet,
        confirm_box: confirmBox,
      });
      setSuccess(true);
      if (res.data.warning) setError(res.data.warning);
      setTimeout(() => onSaved?.(), 900);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         onClick={() => !saving && onClose()}>
      <form onSubmit={submit}
            className="bg-card rounded-xl shadow-xl border border-border w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" /> Enter dimensions manually
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              <span className="font-medium text-foreground">{variant.product_name}</span>
              {variant.variant_name && <> — {variant.variant_name}</>}
              {variant.sku && <> · <span className="font-mono text-xs">{variant.sku}</span></>}
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={saving}
                  className="rounded-md p-1.5 hover:bg-muted disabled:opacity-50">
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {success && (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" /> Saved to Odoo.
            </div>
          )}
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          {/* Boxes */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {boxes.length === 1 ? 'Box dimensions' : `Boxes (${boxes.length})`}
              </label>
              {boxes.length > 1 && (
                <span className="text-[11px] text-muted-foreground tabular-nums">
                  Total: {totalWeight.toFixed(2)} kg · vol {totalVolWeight.toFixed(2)} kg
                </span>
              )}
            </div>
            <div className="space-y-2 mt-1.5">
              {boxes.map((b, i) => (
                <div key={i} className="rounded-lg border border-border p-2.5 bg-muted/10">
                  {boxes.length > 1 && (
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-mono text-muted-foreground">Box {i + 1}</span>
                      <button type="button" onClick={() => removeBox(i)}
                              disabled={saving}
                              className="text-[11px] text-red-600 hover:underline inline-flex items-center gap-1">
                        <Trash2 className="h-3 w-3" /> Remove
                      </button>
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-2">
                    <DimInput label="L" suffix="cm" value={b.length} onChange={(v) => updateBox(i, 'length', v)} disabled={saving} />
                    <DimInput label="W" suffix="cm" value={b.width}  onChange={(v) => updateBox(i, 'width', v)}  disabled={saving} />
                    <DimInput label="H" suffix="cm" value={b.height} onChange={(v) => updateBox(i, 'height', v)} disabled={saving} />
                    <DimInput label="Weight" suffix="kg" value={b.weight} onChange={(v) => updateBox(i, 'weight', v)} disabled={saving} />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addBox} disabled={saving}
                      className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                <Plus className="h-3.5 w-3.5" /> Add another box
              </button>
            </div>
          </div>

          {/* Flags */}
          <div className="space-y-2 pt-1">
            <Toggle label="Fragile" checked={fragile} onChange={setFragile} disabled={saving}
                    hint="Adds protective-packaging surcharge in shipping calc." />
            <Toggle label="Ship on pallet" checked={onPallet} onChange={setOnPallet} disabled={saving}
                    hint="Routes through pallet carriers regardless of size." />
            <Toggle label="Mark box_confirmed" checked={confirmBox} onChange={setConfirmBox} disabled={saving}
                    hint="Only enable after physically verifying the box." emerald />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 flex items-center justify-between gap-3">
          <p className="text-[11px] text-muted-foreground">
            Writes directly to Odoo <span className="font-mono">product.packaging.info</span>.
          </p>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={!canSubmit || saving || success}
                    className="gap-1.5 bg-primary text-primary-foreground">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : success ? <CheckCircle className="h-3.5 w-3.5" />
                      : <Save className="h-3.5 w-3.5" />}
              {saving ? 'Saving…' : success ? 'Saved' : 'Save'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

const DimInput = ({ label, suffix, value, onChange, disabled }) => (
  <div className="rounded-md border border-border bg-card focus-within:ring-2 focus-within:ring-primary/50 transition-shadow">
    <label className="block px-3 pt-1.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
    </label>
    <div className="flex items-center gap-1 px-3 pb-1.5">
      <input type="number" step="0.1" min="0" inputMode="decimal"
             value={value} onChange={(e) => onChange(e.target.value)}
             disabled={disabled}
             className="w-full bg-transparent outline-none font-mono text-sm tabular-nums placeholder:text-muted-foreground/40 disabled:opacity-60"
             placeholder="0.0" />
      <span className="text-xs text-muted-foreground">{suffix}</span>
    </div>
  </div>
);

const Toggle = ({ label, checked, onChange, disabled, hint, emerald }) => (
  <label className={`flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer select-none transition-colors
                     ${checked ? (emerald ? 'border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20'
                                          : 'border-primary/40 bg-primary/5')
                               : 'border-border bg-card hover:bg-muted/40'}
                     ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
    <input type="checkbox" checked={checked} disabled={disabled}
           onChange={(e) => onChange(e.target.checked)}
           className={`h-4 w-4 mt-0.5 rounded border-input ${emerald ? 'accent-emerald-600' : 'accent-primary'}`} />
    <div className="min-w-0">
      <p className="text-sm font-medium">{label}</p>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  </label>
);


const STAT_CHIP_TONES = {
  neutral: {
    idle: 'border-border bg-card text-foreground hover:bg-accent',
    active: 'border-primary bg-primary text-primary-foreground shadow-sm ring-2 ring-primary/30',
  },
  danger: {
    idle: 'border-border bg-card text-foreground hover:border-red-300 hover:bg-red-50 dark:hover:bg-red-950/20',
    active: 'border-red-500 bg-red-500 text-white shadow-sm ring-2 ring-red-500/30',
  },
  warning: {
    idle: 'border-border bg-card text-foreground hover:border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/20',
    active: 'border-amber-500 bg-amber-500 text-white shadow-sm ring-2 ring-amber-500/30',
  },
  info: {
    idle: 'border-border bg-card text-foreground hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/20',
    active: 'border-blue-600 bg-blue-600 text-white shadow-sm ring-2 ring-blue-500/30',
  },
  success: {
    idle: 'border-border bg-card text-foreground hover:border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/20',
    active: 'border-emerald-600 bg-emerald-600 text-white shadow-sm ring-2 ring-emerald-500/30',
  },
};

const StatChip = ({ icon: Icon, label, count, active, onClick, tone = 'neutral', title }) => {
  const tones = STAT_CHIP_TONES[tone] || STAT_CHIP_TONES.neutral;
  const hasCount = count != null;
  return (
    <button
      type="button"
      onClick={onClick}
      title={title || label}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-all whitespace-nowrap ${active ? tones.active : tones.idle}`}
    >
      {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" />}
      {hasCount && <span className="font-semibold tabular-nums">{count.toLocaleString()}</span>}
      <span className={`${active ? 'opacity-90' : 'text-muted-foreground'}`}>{label}</span>
    </button>
  );
};

const ATTR_CHIP_CLASSES = {
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  slate: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

const AttrChip = ({ icon: Icon, color, title }) => (
  <span
    title={title}
    className={`inline-flex items-center justify-center h-5 w-5 rounded-full ${ATTR_CHIP_CLASSES[color]}`}
  >
    <Icon className="h-3 w-3" />
  </span>
);

const AttributesCell = ({ row }) => {
  const chips = [];
  if (row.box_confirmed) chips.push({ icon: CheckCircle, color: 'emerald', title: 'Box confirmed' });
  if (row.fragile) chips.push({ icon: AlertTriangle, color: 'amber', title: 'Fragile' });
  if (row.shipped_on_pallet) chips.push({ icon: Truck, color: 'blue', title: 'Shipped on pallet' });
  if (row.fits_box === false) chips.push({ icon: X, color: 'red', title: "Doesn't fit any standard box" });
  if (row.fits_pallet === false) chips.push({ icon: X, color: 'red', title: "Doesn't fit a pallet" });
  if (chips.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className="flex items-center justify-center gap-1 flex-wrap">
      {chips.map((c, i) => <AttrChip key={i} {...c} />)}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Missing Packaging Data tab — active variants with neither confirmed nor
// Odoo-sourced dimensions. Everything they price with today is a fallback
// estimate. Includes a per-brand coverage report to spot the worst brands.
// -----------------------------------------------------------------------------
// ======================= Import filled sheet (modal) ========================
const IMPORT_FIELD_LABELS = {
  variant_id: 'Shopify Variant ID *',
  odoo_id: 'Odoo ID',
  sku: 'SKU (vendor code)',
  product: 'Product name',
};
const BOX_KEY_LABELS = { l: 'L (cm)', w: 'W (cm)', h: 'H (cm)', wt: 'Weight', ean: 'EAN' };

const ImportStat = ({ label, value, tone }) => (
  <div className={`rounded-lg border p-2 text-center ${
    tone === 'green' ? 'border-green-300 bg-green-50/50 dark:border-green-800 dark:bg-green-950/20'
    : tone === 'red' ? 'border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20'
    : 'border-border'}`}>
    <div className="text-lg font-semibold tabular-nums">{value}</div>
    <div className="text-[10px] text-muted-foreground leading-tight">{label}</div>
  </div>
);

const ImportPackagingModal = ({ open, onClose, onDone }) => {
  const [step, setStep] = useState('upload'); // upload | mapping | preview | result
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [inspect, setInspect] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [confirmOdoo, setConfirmOdoo] = useState(true);
  const fileRef = useRef(null);

  const reset = () => {
    setStep('upload'); setFile(null); setBusy(false); setErr(null);
    setInspect(null); setMapping(null); setPreview(null); setResult(null); setConfirmOdoo(true);
  };
  const close = () => { reset(); onClose(); };

  const post = (f, mp, params) => {
    const fd = new FormData();
    fd.append('file', f);
    if (mp) fd.append('mapping', JSON.stringify(mp));
    return axiosInstance.post('api/packaging-data/missing/brand-import/', fd, { params });
  };

  const onFile = async (f) => {
    if (!f) return;
    setFile(f); setErr(null); setBusy(true);
    try {
      const res = await post(f, null, { mode: 'inspect' });
      const d = res.data;
      if (!d.success) { setErr(d.message || 'Could not read file'); return; }
      setInspect(d); setMapping(d.suggested_mapping);
      if (d.is_standard) { await runPreview(f, d.suggested_mapping); }
      else { setStep('mapping'); }
    } catch (e) { setErr(e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  const runPreview = async (f = file, mp = mapping) => {
    setBusy(true); setErr(null);
    try {
      const res = await post(f, mp, { mode: 'preview' });
      if (!res.data.success) { setErr(res.data.message); return; }
      setPreview(res.data); setStep('preview');
    } catch (e) { setErr(e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  const runApply = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await post(file, mapping, { mode: 'apply', apply: 'true', confirm: confirmOdoo ? 'true' : 'false' });
      setResult(res.data.results || res.data); setStep('result');
    } catch (e) { setErr(e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  if (!open) return null;

  const cols = inspect?.columns || [];
  const writable = preview ? (preview.plan.odoo_and_shopify + preview.plan.shopify_only) : 0;

  const ColSelect = ({ value, onChange }) => (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      className="h-8 rounded-md border border-input bg-background px-2 text-xs w-full">
      <option value="">— none —</option>
      {cols.map((c) => <option key={c.index} value={c.index}>{c.index}: {c.header || '(blank)'}</option>)}
    </select>
  );
  const setIdentity = (k, v) => setMapping((m) => ({ ...m, identity: { ...m.identity, [k]: v } }));
  const setBox = (i, k, v) => setMapping((m) => ({ ...m, boxes: m.boxes.map((g, gi) => gi === i ? { ...g, [k]: v } : g) }));
  const addBox = () => setMapping((m) => ({ ...m, boxes: [...(m.boxes || []), { l: null, w: null, h: null, wt: null, ean: null }] }));
  const rmBox = (i) => setMapping((m) => ({ ...m, boxes: m.boxes.filter((_, gi) => gi !== i) }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={close}>
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2"><Upload className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">Import filled packaging sheet</h3></div>
          <button onClick={close} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 pt-3 text-[11px] text-muted-foreground flex gap-2">
          {['upload', 'mapping', 'preview', 'result'].map((s, i) => (
            <span key={s} className={step === s ? 'text-primary font-medium' : ''}>{i + 1}. {s}{i < 3 ? ' ›' : ''}</span>
          ))}
        </div>

        <div className="p-5 overflow-y-auto">
          {err && <div className="mb-3 rounded-md bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 text-xs px-3 py-2">{err}</div>}
          {busy && <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Working…</div>}

          {step === 'upload' && (
            <div>
              <p className="text-sm text-muted-foreground mb-3">Upload a filled export sheet, or any supplier .xlsx (you can map its columns in the next step). Nothing is written until you review the preview and confirm. The decision per row: Odoo already has packaging leaves it untouched; Odoo linked but empty writes both Odoo and Shopify; no Odoo link writes the Shopify metafield only.</p>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              <button onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-border rounded-xl py-10 text-sm text-muted-foreground hover:border-primary hover:text-primary transition">
                <Upload className="h-6 w-6 mx-auto mb-2" /> Choose .xlsx file
              </button>
            </div>
          )}

          {step === 'mapping' && mapping && (
            <div className="space-y-4">
              <div className="text-xs text-muted-foreground">Sheet <b>{inspect?.sheet}</b>, headers on row {(mapping.header_row ?? 0) + 1}. This is not the standard template, so map your columns onto our fields (suggestions pre-filled).</div>
              <div className="space-y-2">
                <div className="text-xs font-medium">Identity</div>
                {Object.keys(IMPORT_FIELD_LABELS).map((k) => (
                  <div key={k} className="grid grid-cols-[170px_1fr] items-center gap-2">
                    <label className="text-xs">{IMPORT_FIELD_LABELS[k]}</label>
                    <ColSelect value={mapping.identity?.[k]} onChange={(v) => setIdentity(k, v)} />
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium">Boxes (one group per box)</div>
                  <button onClick={addBox} className="text-xs text-primary flex items-center gap-1"><Plus className="h-3 w-3" /> Add box</button>
                </div>
                {(mapping.boxes || []).map((g, i) => (
                  <div key={i} className="rounded-lg border border-border p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] text-muted-foreground">Box {i + 1}</span>
                      <button onClick={() => rmBox(i)} className="text-muted-foreground hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                    </div>
                    <div className="grid grid-cols-5 gap-1">
                      {['l', 'w', 'h', 'wt', 'ean'].map((k) => (
                        <div key={k}>
                          <div className="text-[10px] text-muted-foreground mb-0.5">{BOX_KEY_LABELS[k]}</div>
                          <ColSelect value={g[k]} onChange={(v) => setBox(i, k, v)} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="outline" size="sm" onClick={() => setStep('upload')}>Back</Button>
                <Button size="sm" disabled={busy || mapping.identity?.variant_id == null} onClick={() => runPreview()}>Preview</Button>
              </div>
            </div>
          )}

          {step === 'preview' && preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <ImportStat label="Will write" value={writable} tone="green" />
                <ImportStat label="Skip (Odoo has data)" value={preview.plan.skip_odoo_has_data} />
                <ImportStat label="No boxes" value={preview.plan.no_boxes} />
                <ImportStat label="Blocked (errors)" value={preview.plan.blocked} tone={preview.plan.blocked ? 'red' : undefined} />
              </div>
              <div className="text-xs">
                <span className="text-red-600 font-medium">{preview.quality.errors} errors</span> ·{' '}
                <span className="text-amber-600 font-medium">{preview.quality.warnings} warnings</span> across {preview.total_rows} rows
              </div>
              {preview.quality.top_issues?.length > 0 && (
                <div className="rounded-lg border border-border p-2 text-[11px] space-y-0.5 max-h-32 overflow-y-auto">
                  {preview.quality.top_issues.map(([msg, n], i) => <div key={i}><b>{n}×</b> {msg}</div>)}
                </div>
              )}
              <div className="max-h-52 overflow-y-auto rounded-lg border border-border">
                <table className="w-full text-[11px]">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr><th className="text-left px-2 py-1">Product</th><th className="text-left px-2 py-1">Variant</th>
                      <th className="px-2 py-1">Target</th><th className="px-2 py-1">Boxes</th><th className="text-left px-2 py-1">Issues</th></tr>
                  </thead>
                  <tbody>
                    {preview.to_write.slice(0, 100).map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-2 py-1 truncate max-w-[180px]">{r.product}</td>
                        <td className="px-2 py-1 font-mono">{r.vid}</td>
                        <td className="px-2 py-1 text-center">{r.target}</td>
                        <td className="px-2 py-1 text-center">{r.boxes}</td>
                        <td className="px-2 py-1 text-amber-600">{r.issues.map((x) => x.msg).join('; ')}</td>
                      </tr>
                    ))}
                    {preview.blocked.map((r, i) => (
                      <tr key={'b' + i} className="border-t border-border bg-red-50/50 dark:bg-red-950/20">
                        <td className="px-2 py-1 truncate max-w-[180px]">{r.product}</td>
                        <td className="px-2 py-1 font-mono">{r.vid || '—'}</td>
                        <td className="px-2 py-1 text-center">blocked</td>
                        <td className="px-2 py-1 text-center">{r.boxes}</td>
                        <td className="px-2 py-1 text-red-600">{r.issues.map((x) => x.msg).join('; ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={confirmOdoo} onChange={(e) => setConfirmOdoo(e.target.checked)} />
                Mark Odoo boxes as confirmed (box_confirmed)
              </label>
              <div className="flex justify-between pt-1">
                <Button variant="outline" size="sm" onClick={() => setStep(inspect?.is_standard ? 'upload' : 'mapping')}>Back</Button>
                <Button size="sm" disabled={busy || writable === 0} onClick={runApply}>
                  Apply {writable} write{writable === 1 ? '' : 's'}
                </Button>
              </div>
            </div>
          )}

          {step === 'result' && result && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm"><CheckCircle className="h-5 w-5 text-green-600" /> Import complete</div>
              <div className="grid grid-cols-3 gap-2">
                <ImportStat label="Odoo written" value={result.odoo_written} tone="green" />
                <ImportStat label="Shopify written" value={result.shopify_written} tone="green" />
                <ImportStat label="Errors" value={result.errors?.length || 0} tone={result.errors?.length ? 'red' : undefined} />
              </div>
              {result.errors?.length > 0 && (
                <div className="rounded-lg border border-border p-2 text-[11px] max-h-32 overflow-y-auto">
                  {result.errors.map((e, i) => <div key={i}><span className="font-mono">{e.vid}</span>: <span className="text-red-600">{e.error}</span></div>)}
                </div>
              )}
              <div className="flex justify-end"><Button size="sm" onClick={() => { onDone && onDone(); close(); }}>Done</Button></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MissingPackagingTab = () => {
  const PAGE_SIZE = 50;
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [noDimsOnly, setNoDimsOnly] = useState(false);
  const [linkedFilter, setLinkedFilter] = useState('all'); // all | true | false
  const [sortField, setSortField] = useState('brand');
  const [sortDir, setSortDir] = useState('asc');

  const [report, setReport] = useState(null); // { brands: [...], totals: {...} }
  const [reportLoading, setReportLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(true);
  const [brandReportSearch, setBrandReportSearch] = useState('');
  const [brandReportPage, setBrandReportPage] = useState(1);
  const [exportingBrand, setExportingBrand] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const BRAND_PAGE_SIZE = 12;

  const [manualFor, setManualFor] = useState(null);
  const [proposalFor, setProposalFor] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchReport = useCallback(async () => {
    setReportLoading(true);
    try {
      const res = await axiosInstance.get('api/packaging-data/missing/brand-report/');
      setReport(res.data);
    } catch {
      // Report is non-critical; the table still works without it
    } finally {
      setReportLoading(false);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        sort_by: sortField,
        sort_dir: sortDir,
      };
      if (searchDebounced) params.search = searchDebounced;
      if (brandFilter) params.brand = brandFilter;
      if (noDimsOnly) params.no_dims = 'true';
      if (linkedFilter !== 'all') params.linked = linkedFilter;
      const res = await axiosInstance.get('api/packaging-data/missing/', { params });
      setRows(res.data.data || []);
      setTotalCount(res.data.pagination?.total_count || 0);
      setTotalPages(res.data.pagination?.total_pages || 1);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [page, sortField, sortDir, searchDebounced, brandFilter, noDimsOnly, linkedFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetchReport(); }, [fetchReport]);

  const refreshAll = () => { fetchRows(); fetchReport(); };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 text-primary" />
      : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
  };

  const selectBrand = (brand) => {
    setBrandFilter(prev => (prev === brand ? '' : brand));
    setPage(1);
  };

  const fmtPrice = (p) => {
    const n = parseFloat(p);
    return Number.isFinite(n) ? `€${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—';
  };

  const totals = report?.totals;
  const reportBrands = (report?.brands || []).filter(b => b.missing > 0);
  const brandReportFiltered = reportBrands.filter(
    b => b.brand.toLowerCase().includes(brandReportSearch.trim().toLowerCase()));
  const brandReportPages = Math.max(1, Math.ceil(brandReportFiltered.length / BRAND_PAGE_SIZE));
  const brandReportPageSafe = Math.min(brandReportPage, brandReportPages);
  const brandReportPaged = brandReportFiltered.slice(
    (brandReportPageSafe - 1) * BRAND_PAGE_SIZE, brandReportPageSafe * BRAND_PAGE_SIZE);

  const exportBrand = async (brand) => {
    const ans = window.prompt(
      'How many boxes per product to include in the sheet?\n\n' +
      'Most products ship in 1 box; use more for multi-box / large items (up to 25). ' +
      'Extra columns can be left blank.',
      '3');
    if (ans === null) return; // cancelled
    let boxes = parseInt(ans, 10);
    if (!Number.isFinite(boxes) || boxes < 1) boxes = 3;
    boxes = Math.min(boxes, 25);
    setExportingBrand(brand);
    try {
      const res = await axiosInstance.get('api/packaging-data/missing/brand-export/', {
        params: { brand, boxes }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const safe = brand.replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || 'brand';
      a.download = `packaging_${safe}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + (e.response?.status || e.message));
    } finally {
      setExportingBrand(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* ========== Hero header ========== */}
      <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/5 via-card to-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 max-w-2xl">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-primary/10 p-2">
                <PackageX className="h-5 w-5 text-primary" />
              </div>
              <h2 className="text-xl font-semibold">Missing packaging data</h2>
            </div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Active variants with neither packer-confirmed boxes nor Odoo dimensions.
              Anything these products price at checkout today comes from the fallback chain
              (sibling variant, brand averages) or, worst case, the global average.
              Variants without an Odoo link need a product record there before
              dimensions can be entered; the rest can be fixed right away.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" onClick={() => setImportOpen(true)}>
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              Import filled sheet
            </Button>
            <Button variant="outline" size="sm" onClick={refreshAll} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <KpiPill icon={PackageX} label="Variants missing" value={totals ? totals.missing.toLocaleString() : '…'} tone="red" />
          <KpiPill icon={CheckCircle} label="Catalog coverage" value={totals ? `${totals.coverage_pct}%` : '…'} />
          <KpiPill icon={Layers} label="Brands affected" value={totals ? totals.brands_affected : '…'} tone="amber" />
          <KpiPill icon={AlertTriangle} label="No Odoo link" value={totals ? totals.missing_unlinked.toLocaleString() : '…'} tone="amber" />
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      {/* ========== Brand report ========== */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <button type="button"
                onClick={() => setReportOpen(o => !o)}
                className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-muted/30 transition-colors">
          <span className="flex items-center gap-2 text-sm font-semibold">
            <BarChart3 className="h-4 w-4 text-primary" />
            Missing data by brand
            {reportBrands.length > 0 && (
              <span className="text-xs font-normal text-muted-foreground">
                {reportBrands.length} brands with gaps
              </span>
            )}
          </span>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${reportOpen ? 'rotate-180' : ''}`} />
        </button>
        {reportOpen && (
          <div className="border-t border-border">
            {reportLoading ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                <RefreshCw className="h-5 w-5 mx-auto mb-2 animate-spin opacity-40" />
                Loading brand report…
              </div>
            ) : reportBrands.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">No gaps — every brand fully covered 🎉</div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-border/50">
                  <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input value={brandReportSearch}
                           onChange={(e) => { setBrandReportSearch(e.target.value); setBrandReportPage(1); }}
                           placeholder="Search brand…"
                           className="pl-9 h-9" />
                    {brandReportSearch && (
                      <button onClick={() => { setBrandReportSearch(''); setBrandReportPage(1); }}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        <XIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {brandReportFiltered.length} brand{brandReportFiltered.length !== 1 ? 's' : ''} with gaps
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-card">
                      <tr className="border-b border-border text-xs text-muted-foreground">
                        <th className="text-left font-medium px-5 py-2">Brand</th>
                        <th className="text-right font-medium px-3 py-2">Missing</th>
                        <th className="text-right font-medium px-3 py-2" title="Missing variants with no Odoo product — dims cannot be entered until they exist in Odoo">No Odoo</th>
                        <th className="text-right font-medium px-3 py-2">Confirmed</th>
                        <th className="text-right font-medium px-3 py-2">Odoo unconf.</th>
                        <th className="text-right font-medium px-3 py-2">Total</th>
                        <th className="text-left font-medium px-5 py-2 w-[22%]">Coverage</th>
                        <th className="text-right font-medium px-4 py-2">Export</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brandReportPaged.length === 0 ? (
                        <tr><td colSpan={8} className="px-5 py-8 text-center text-sm text-muted-foreground">No brands match “{brandReportSearch}”.</td></tr>
                      ) : brandReportPaged.map(b => {
                        const active = brandFilter === b.brand;
                        return (
                          <tr key={b.brand}
                              onClick={() => selectBrand(b.brand)}
                              title={active ? 'Click to clear brand filter' : `Filter table on ${b.brand}`}
                              className={`border-b border-border/50 cursor-pointer transition-colors ${active ? 'bg-primary/10' : 'hover:bg-muted/30'}`}>
                            <td className="px-5 py-2 font-medium whitespace-nowrap">
                              {b.brand}
                              {active && <span className="ml-2 text-[10px] text-primary font-semibold">filtering</span>}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-600 dark:text-red-400">{b.missing}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-amber-700 dark:text-amber-400">{b.missing_unlinked || 0}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-emerald-700 dark:text-emerald-400">{b.confirmed}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{b.odoo_unconfirmed}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{b.total}</td>
                            <td className="px-5 py-2">
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                  <div className={`h-full rounded-full ${b.coverage_pct >= 80 ? 'bg-emerald-500' : b.coverage_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                                       style={{ width: `${b.coverage_pct}%` }} />
                                </div>
                                <span className="text-xs tabular-nums text-muted-foreground w-12 text-right">{b.coverage_pct}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                              <Button variant="outline" size="sm" className="h-7 px-2"
                                      disabled={exportingBrand === b.brand}
                                      onClick={() => exportBrand(b.brand)}
                                      title={`Export ${b.brand} packaging sheet (Excel, one row per variant, columns per box)`}>
                                {exportingBrand === b.brand
                                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  : <Download className="h-3.5 w-3.5" />}
                                <span className="ml-1 text-xs">Excel</span>
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {brandReportPages > 1 && (
                  <div className="flex items-center justify-between px-5 py-3 border-t border-border/50 text-xs text-muted-foreground">
                    <span>Page {brandReportPageSafe} of {brandReportPages}</span>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                              disabled={brandReportPageSafe <= 1}
                              onClick={() => setBrandReportPage(p => Math.max(1, p - 1))}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 w-7 p-0"
                              disabled={brandReportPageSafe >= brandReportPages}
                              onClick={() => setBrandReportPage(p => Math.min(brandReportPages, p + 1))}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ========== Filters ========== */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search SKU, product or brand…"
                 className="pl-9" />
          {search && (
            <button onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {brandFilter && (
          <button type="button"
                  onClick={() => selectBrand(brandFilter)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1.5 text-xs font-medium hover:bg-primary/20 transition-colors">
            {brandFilter} <X className="h-3 w-3" />
          </button>
        )}
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none rounded-md border border-border bg-card px-2.5 py-2">
          <input type="checkbox" checked={noDimsOnly}
                 onChange={(e) => { setNoDimsOnly(e.target.checked); setPage(1); }}
                 className="h-3.5 w-3.5 rounded border-input accent-primary" />
          No fallback dims either
        </label>
        <select value={linkedFilter}
                onChange={(e) => { setLinkedFilter(e.target.value); setPage(1); }}
                className="rounded-md border border-border bg-card px-2.5 py-2 text-xs text-muted-foreground cursor-pointer">
          <option value="all">Odoo link: all</option>
          <option value="true">Linked (dims can be entered)</option>
          <option value="false">No Odoo product yet</option>
        </select>
        <div className="flex-1" />
        <p className="text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">{totalCount.toLocaleString()}</span> variants
        </p>
      </div>

      {/* ========== Table ========== */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-xs text-muted-foreground">
                <th className="text-left font-medium px-4 py-2.5 cursor-pointer select-none" onClick={() => handleSort('product_name')}>
                  <span className="inline-flex items-center">Product <SortIcon field="product_name" /></span>
                </th>
                <th className="text-left font-medium px-3 py-2.5 cursor-pointer select-none" onClick={() => handleSort('brand')}>
                  <span className="inline-flex items-center">Brand <SortIcon field="brand" /></span>
                </th>
                <th className="text-left font-medium px-3 py-2.5">Type</th>
                <th className="text-left font-medium px-3 py-2.5">Current estimate</th>
                <th className="text-left font-medium px-3 py-2.5">PIM product info</th>
                <th className="text-right font-medium px-3 py-2.5 cursor-pointer select-none" onClick={() => handleSort('stock')}>
                  <span className="inline-flex items-center">Stock <SortIcon field="stock" /></span>
                </th>
                <th className="text-right font-medium px-3 py-2.5 cursor-pointer select-none" onClick={() => handleSort('orders_6m')}>
                  <span className="inline-flex items-center">Orders 6m <SortIcon field="orders_6m" /></span>
                </th>
                <th className="text-right font-medium px-3 py-2.5 cursor-pointer select-none" onClick={() => handleSort('price')}>
                  <span className="inline-flex items-center">Price <SortIcon field="price" /></span>
                </th>
                <th className="text-center font-medium px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="py-16 text-center text-muted-foreground">
                    <RefreshCw className="h-6 w-6 mx-auto mb-3 animate-spin opacity-40" />
                    Loading missing variants…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-16 text-center text-muted-foreground">
                    <Package className="h-8 w-8 mx-auto mb-3 opacity-40" />
                    No variants match the current filters.
                  </td>
                </tr>
              ) : rows.map(r => {
                const badge = DIM_SOURCE_BADGES[r.dimension_source];
                return (
                  <tr key={r.shopify_variant_id} className="border-b border-border/50 hover:bg-muted/20 transition-colors align-top">
                    {/* Product */}
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-muted/40 overflow-hidden flex items-center justify-center shrink-0 border border-border/60">
                          {r.image ? (
                            <img src={r.image} alt="" loading="lazy"
                                 className="w-full h-full object-cover"
                                 onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          ) : (
                            <Package className="h-4 w-4 text-muted-foreground opacity-50" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium leading-snug truncate max-w-[26rem]" title={r.product_name}>{r.product_name || '—'}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[26rem]">
                            {r.variant_name && <span>{r.variant_name} · </span>}
                            {r.sku ? (
                              <span className="font-mono text-[11px]">{r.sku}</span>
                            ) : (
                              <span className="text-[10px] text-amber-700 dark:text-amber-400 font-medium" title="No SKU in the PIM — variant is not linked to an Odoo product">no SKU</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </td>
                    {/* Brand */}
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <button type="button" onClick={() => selectBrand(r.brand)}
                              className="hover:text-primary hover:underline underline-offset-2 transition-colors"
                              title={`Filter on ${r.brand}`}>
                        {r.brand}
                      </button>
                    </td>
                    {/* Type */}
                    <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{r.product_type || '—'}</td>
                    {/* Current estimate (fallback boxes) */}
                    <td className="px-3 py-2.5">
                      {r.fallback_boxes.length > 0 ? (
                        <div className="space-y-0.5">
                          {r.fallback_boxes.map((b, i) => {
                            const bBadge = DIM_SOURCE_BADGES[b.dimension_source];
                            return (
                              <div key={i} className="flex items-center gap-1.5 whitespace-nowrap">
                                <span className="font-mono text-xs">
                                  {b.length}×{b.width}×{b.height} · {b.weight} kg
                                </span>
                                {bBadge && (
                                  <span title={bBadge.tooltip}
                                        className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-medium ${DIM_SOURCE_TONE_BADGE[bBadge.tone]}`}>
                                    {bBadge.label}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 text-[10px] font-medium whitespace-nowrap">
                          <AlertTriangle className="h-2.5 w-2.5" /> No dims
                        </span>
                      )}
                    </td>
                    {/* PIM product info hint */}
                    <td className="px-3 py-2.5 text-xs text-muted-foreground max-w-[12rem]">
                      {r.pim_dimensions || r.pim_weight ? (
                        <div className="space-y-0.5">
                          {r.pim_dimensions && <p className="truncate" title={r.pim_dimensions}>{r.pim_dimensions}</p>}
                          {r.pim_weight && <p className="truncate" title={`Product weight (PIM): ${r.pim_weight}`}>{r.pim_weight} kg</p>}
                        </div>
                      ) : '—'}
                    </td>
                    {/* Stock */}
                    <td className={`px-3 py-2.5 text-right tabular-nums ${r.stock > 0 ? 'font-medium' : 'text-muted-foreground/60'}`}>{r.stock}</td>
                    {/* Orders 6m */}
                    <td className={`px-3 py-2.5 text-right tabular-nums ${r.orders_6m > 0 ? 'font-medium' : 'text-muted-foreground/60'}`}>{r.orders_6m}</td>
                    {/* Price */}
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{fmtPrice(r.price)}</td>
                    {/* Actions */}
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-center gap-1">
                        {r.has_fallback_dims && r.odoo_id && (
                          <button type="button"
                                  onClick={() => setProposalFor(r)}
                                  className="inline-flex items-center gap-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20 px-2 py-1 text-[11px] font-medium transition-colors"
                                  title="Inherit dims from a confirmed sibling variant">
                            <Lightbulb className="h-3 w-3" /> Propose
                          </button>
                        )}
                        {r.odoo_id && (
                          <button type="button"
                                  onClick={() => setManualFor(r)}
                                  className="inline-flex items-center gap-1 rounded-md border border-border text-foreground/80 hover:bg-muted px-2 py-1 text-[11px] font-medium transition-colors"
                                  title="Enter dimensions manually">
                            <Pencil className="h-3 w-3" /> Enter
                          </button>
                        )}
                        {r.odoo_id && (
                          <a href={`${ODOO_BASE_URL}/odoo/action-143/${r.odoo_id}`}
                             target="_blank" rel="noopener noreferrer" title="Open variant in Odoo"
                             className="inline-flex items-center rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                        {r.shopify_product_id && (
                          <a href={`https://admin.shopify.com/store/${SHOPIFY_STORE}/products/${r.shopify_product_id}`}
                             target="_blank" rel="noopener noreferrer" title="Open product in Shopify admin"
                             className="inline-flex items-center rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                            <Globe className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
            <p className="text-xs text-muted-foreground">
              Page <span className="font-semibold text-foreground">{page}</span> of {totalPages.toLocaleString()}
              <span className="mx-1.5">·</span>{totalCount.toLocaleString()} variants
            </p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}>
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {proposalFor && (
        <ProposalModal
          variant={proposalFor}
          onClose={() => setProposalFor(null)}
          onApplied={() => { setProposalFor(null); refreshAll(); }}
        />
      )}
      {manualFor && (
        <ManualEntryModal
          variant={manualFor}
          onClose={() => setManualFor(null)}
          onSaved={() => { setManualFor(null); refreshAll(); }}
        />
      )}
      <ImportPackagingModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={refreshAll}
      />
    </div>
  );
};

const PackagingData = () => {
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'packaging');

  // Data state
  const [data, setData] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncResult, setSyncResult] = useState(null); // {success, message, stats} or {error, message}
  const [syncElapsed, setSyncElapsed] = useState(0);
  const syncTimerRef = useRef(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Search and filters
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [dimensionFilter, setDimensionFilter] = useState('all');
  const [confirmedFilter, setConfirmedFilter] = useState('all');
  const [fitsBoxFilter, setFitsBoxFilter] = useState('all');
  const [fitsPalletFilter, setFitsPalletFilter] = useState('all');
  const [fragileFilter, setFragileFilter] = useState(false);
  const [palletModeFilter, setPalletModeFilter] = useState(false);
  const [heavyFilter, setHeavyFilter] = useState(false);
  const [outliersFilter, setOutliersFilter] = useState(false);
  const [missingCostFilter, setMissingCostFilter] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('all');

  // Sorting
  const [sortField, setSortField] = useState('product_name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Column resizing — percentage-based so table fills full width
  const defaultPcts = {
    productId: 9, product: 28, box: 5, dims: 15, weight: 8, volWeight: 8, cost: 9, attributes: 18,
  };
  const [colPcts, setColPcts] = useState(defaultPcts);
  const resizingCol = useRef(null);
  const resizeStartX = useRef(0);
  const resizeStartPct = useRef(0);
  const tableRef = useRef(null);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!resizingCol.current || !tableRef.current) return;
      const tableWidth = tableRef.current.offsetWidth;
      const diffPx = e.clientX - resizeStartX.current;
      const diffPct = (diffPx / tableWidth) * 100;
      const newPct = Math.max(3, resizeStartPct.current + diffPct);
      setColPcts(prev => ({ ...prev, [resizingCol.current]: newPct }));
    };
    const onMouseUp = () => { resizingCol.current = null; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const startResize = (colKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    resizingCol.current = colKey;
    resizeStartX.current = e.clientX;
    resizeStartPct.current = colPcts[colKey];
  };

  // Shipping estimate modal
  const [estimateModalOpen, setEstimateModalOpen] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateData, setEstimateData] = useState(null);
  const [estimateError, setEstimateError] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [badgesPanelOpen, setBadgesPanelOpen] = useState(false);
  const [expandedEstCountry, setExpandedEstCountry] = useState(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Auto-dismiss success
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axiosInstance.get('api/packaging-data/stats/');
      const s = response.data.stats || response.data;
      if (s.dimension_coverage !== undefined && s.dimension_coverage_percent === undefined) {
        s.dimension_coverage_percent = s.dimension_coverage;
      }
      setStats(s);
    } catch {
      // Stats are non-critical, silently fail
    }
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        page_size: pageSize,
        sort_by: sortField,
        sort_dir: sortDirection,
      };
      if (searchDebounced) params.search = searchDebounced;
      if (dimensionFilter === 'with_dims') params.has_dimensions = 'true';
      if (dimensionFilter === 'without_dims') params.has_dimensions = 'false';
      if (confirmedFilter === 'confirmed') params.box_confirmed = 'true';
      if (confirmedFilter === 'unconfirmed') params.box_confirmed = 'false';
      if (fitsBoxFilter !== 'all') params.fits_box = fitsBoxFilter;
      if (fitsPalletFilter !== 'all') params.fits_pallet = fitsPalletFilter;
      if (fragileFilter) params.fragile = 'true';
      if (palletModeFilter) params.shipped_on_pallet = 'true';
      if (heavyFilter) params.heavy = 'true';
      if (outliersFilter) params.outliers = 'true';
      if (missingCostFilter) params.missing_cost = 'true';
      if (sourceFilter !== 'all') params.dimension_source = sourceFilter;

      const response = await axiosInstance.get('api/packaging-data/', { params });
      setData(response.data.data || response.data.results || []);
      setTotalCount(response.data.pagination?.total_count || response.data.count || 0);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load packaging data');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortField, sortDirection, searchDebounced, dimensionFilter, confirmedFilter, fitsBoxFilter, fitsPalletFilter, fragileFilter, palletModeFilter, heavyFilter, outliersFilter, missingCostFilter, sourceFilter]);

  useEffect(() => {
    setSelectedIds(new Set());
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Sync from Odoo
  const handleSync = async () => {
    setSyncing(true);
    setSyncModalOpen(true);
    setSyncResult(null);
    setSyncElapsed(0);
    setError(null);
    setSuccess(null);

    // Start elapsed timer
    const startTime = Date.now();
    syncTimerRef.current = setInterval(() => {
      setSyncElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    try {
      const response = await axiosInstance.post('api/packaging-data/sync/');
      setSyncResult({ success: true, message: response.data.message, stats: response.data.stats });
      fetchData();
      fetchStats();
    } catch (err) {
      setSyncResult({ error: true, message: err.response?.data?.message || err.message || 'Sync failed' });
    } finally {
      setSyncing(false);
      clearInterval(syncTimerRef.current);
      syncTimerRef.current = null;
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { if (syncTimerRef.current) clearInterval(syncTimerRef.current); };
  }, []);

  // Sorting handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1);
  };

  // Shipping estimate click handler
  const handleRowClick = async (row) => {
    setEstimateModalOpen(true);
    setEstimateLoading(true);
    setEstimateError(null);
    setEstimateData(null);
    setCountrySearch('');
    try {
      const response = await axiosInstance.get(`api/packaging-data/${row.id}/shipping-estimate/`);
      setEstimateData(response.data);
    } catch (err) {
      setEstimateError(err.response?.data?.message || err.message || 'Failed to load shipping estimates');
    } finally {
      setEstimateLoading(false);
    }
  };

  // Bulk selection helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === data.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data.map(r => r.id)));
    }
  };
  const handleBulkUpdate = async (fields) => {
    if (selectedIds.size === 0) return;
    setBulkUpdating(true);
    try {
      const response = await axiosInstance.put('api/packaging-data/bulk-update/', {
        ids: [...selectedIds],
        fields,
      });
      if (response.data.status === 'success') {
        const odooMsg = response.data.odoo_updated
          ? ` (${response.data.odoo_updated} synced to Odoo)`
          : '';
        const warning = response.data.odoo_warning || '';
        setSuccess(`Updated ${response.data.updated} product(s)${odooMsg}`);
        if (warning) setError(warning);
        setSelectedIds(new Set());
        fetchData();
        fetchStats();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Bulk update failed');
    } finally {
      setBulkUpdating(false);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 text-primary" />
      : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // Helpers
  const hasDimensions = (row) => row.width > 0 && row.height > 0 && row.length > 0;

  const formatDimensions = (row) => {
    if (!hasDimensions(row)) return null;
    return `${row.length.toFixed(0)} x ${row.width.toFixed(0)} x ${row.height.toFixed(0)}`;
  };

  const getOutlierReasons = (row) => {
    const reasons = [];
    const l = row.length || 0, w = row.width || 0, h = row.height || 0;
    const wt = row.weight || 0, vw = row.vol_weight || 0;
    const hasDims = l > 0 && w > 0 && h > 0;

    if (hasDims && wt > 0 && vw > 0) {
      if (vw / wt > 15) reasons.push('Vol. weight is ' + (vw / wt).toFixed(0) + 'x actual weight');
      if (wt / vw > 8) reasons.push('Weight is ' + (wt / vw).toFixed(0) + 'x vol. weight');
    }
    if (Math.max(l, w, h) > 200) reasons.push('Dimension > 200cm');
    if (wt > 80) reasons.push('Weight > 80kg');
    return reasons;
  };

  return (
    <div className="px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-foreground">
            <Database className="h-7 w-7 text-primary" />
            Boxopedia
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Product dimensions and packaging information synced from Odoo.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchData(); fetchStats(); }} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={handleSync} disabled={syncing}>
            {syncing ? <ShippySpinner size={18} className="mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {syncing ? 'Syncing...' : 'Sync from Odoo'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-border">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'packaging' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setActiveTab('packaging')}
        >
          <Database className="h-4 w-4 inline-block mr-1.5 -mt-0.5" /> Packaging Data
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'stacking' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setActiveTab('stacking')}
        >
          <Layers className="h-4 w-4 inline-block mr-1.5 -mt-0.5" /> Stacking Rules
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'upright' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setActiveTab('upright')}
        >
          <ArrowUp className="h-4 w-4 inline-block mr-1.5 -mt-0.5" /> Upright Rules
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'popular' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setActiveTab('popular')}
        >
          <TrendingUp className="h-4 w-4 inline-block mr-1.5 -mt-0.5" /> Popular &amp; Missing
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'missing' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setActiveTab('missing')}
        >
          <PackageX className="h-4 w-4 inline-block mr-1.5 -mt-0.5" /> Missing Packaging Data
        </button>
      </div>

      {activeTab === 'stacking' ? (
        <StackingRulesTab />
      ) : activeTab === 'upright' ? (
        <UprightRulesTab />
      ) : activeTab === 'popular' ? (
        <PopularMissingTab />
      ) : activeTab === 'missing' ? (
        <MissingPackagingTab />
      ) : (
      <>
      {/* Alerts */}
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-2"><X className="h-4 w-4" /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200 flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-2"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Quick-filter chip rail */}
      {(() => {
        const fallbackCount = stats?.dimension_sources
          ? (stats.dimension_sources.shopify_fallback || 0)
            + (stats.dimension_sources.variant_fallback || 0)
            + (stats.dimension_sources.sc_fallback || 0)
            + (stats.dimension_sources.product_type_brand_avg || 0)
            + (stats.dimension_sources.brand_tag_overlap_avg || 0)
            + (stats.dimension_sources.product_type_global_avg || 0)
            + (stats.dimension_sources.global_tag_overlap_avg || 0)
            + (stats.dimension_sources.brand_average || 0)
            + (stats.dimension_sources.global_average || 0)
          : null;
        const noDimsCount = stats?.without_dimensions ?? (
          stats?.total_records != null && stats?.with_dimensions != null
            ? Math.max(0, stats.total_records - stats.with_dimensions)
            : null
        );
        const filterCount =
          (dimensionFilter !== 'all' ? 1 : 0) +
          (confirmedFilter !== 'all' ? 1 : 0) +
          (fitsBoxFilter !== 'all' ? 1 : 0) +
          (fitsPalletFilter !== 'all' ? 1 : 0) +
          (sourceFilter !== 'all' ? 1 : 0) +
          (fragileFilter ? 1 : 0) +
          (palletModeFilter ? 1 : 0) +
          (heavyFilter ? 1 : 0) +
          (outliersFilter ? 1 : 0) +
          (missingCostFilter ? 1 : 0) +
          (searchDebounced ? 1 : 0);
        const resetAll = () => {
          setSearch('');
          setDimensionFilter('all');
          setConfirmedFilter('all');
          setFitsBoxFilter('all');
          setFitsPalletFilter('all');
          setSourceFilter('all');
          setFragileFilter(false);
          setPalletModeFilter(false);
          setHeavyFilter(false);
          setOutliersFilter(false);
          setMissingCostFilter(false);
          setPage(1);
        };
        const toggleSel = (cur, target, setter) => {
          setter(cur === target ? 'all' : target);
          setPage(1);
        };
        return (
          <div className="mb-4 flex flex-wrap items-center gap-1.5">
            <StatChip
              icon={Database}
              label="All products"
              count={stats?.total_records}
              tone="neutral"
              active={filterCount === 0}
              onClick={resetAll}
              title="Clear all filters"
            />
            <StatChip
              icon={AlertTriangle}
              label="No dims"
              count={noDimsCount}
              tone="danger"
              active={dimensionFilter === 'without_dims'}
              onClick={() => toggleSel(dimensionFilter, 'without_dims', setDimensionFilter)}
              title="Products missing dimensions"
            />
            <StatChip
              icon={Layers}
              label="Fallback dims"
              count={fallbackCount}
              tone="info"
              active={sourceFilter === 'fallback'}
              onClick={() => toggleSel(sourceFilter, 'fallback', setSourceFilter)}
              title="Dimensions estimated from sibling, brand or global average"
            />
            <StatChip
              icon={X}
              label="No fit box"
              count={stats?.doesnt_fit_box}
              tone="danger"
              active={fitsBoxFilter === 'doesnt_fit'}
              onClick={() => toggleSel(fitsBoxFilter, 'doesnt_fit', setFitsBoxFilter)}
              title="Doesn't fit any standard box"
            />
            <StatChip
              icon={Truck}
              label="On pallet"
              count={stats?.shipped_on_pallet}
              tone="info"
              active={palletModeFilter}
              onClick={() => { setPalletModeFilter(!palletModeFilter); setPage(1); }}
              title="Products shipped on a pallet"
            />
            <StatChip
              icon={AlertTriangle}
              label="Fragile"
              count={stats?.fragile_products}
              tone="warning"
              active={fragileFilter}
              onClick={() => { setFragileFilter(!fragileFilter); setPage(1); }}
              title="Products marked fragile"
            />
            <StatChip
              icon={AlertTriangle}
              label="Outliers"
              count={stats?.outliers}
              tone="danger"
              active={outliersFilter}
              onClick={() => { setOutliersFilter(!outliersFilter); setPage(1); }}
              title="Suspicious weight/dimension combos — likely data entry errors"
            />
            <StatChip
              icon={AlertTriangle}
              label="No cost"
              count={stats?.missing_cost}
              tone="danger"
              active={missingCostFilter}
              onClick={() => { setMissingCostFilter(!missingCostFilter); setPage(1); }}
              title="Products missing unit cost"
            />
            {filterCount > 0 && (
              <button
                onClick={resetAll}
                className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-dashed border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
                title="Reset all filters"
              >
                <X className="h-3.5 w-3.5" />
                Reset {filterCount} filter{filterCount === 1 ? '' : 's'}
              </button>
            )}
          </div>
        );
      })()}

      {/* Filters and Search */}
      <div className="rounded-xl border border-border/70 surface-panel shadow-sm">
        <div className="px-4 py-2.5 border-b border-border">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative min-w-[200px] flex-1 max-w-[320px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search ID, name or barcode..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-xs"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  title="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <select
              className="h-8 rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={confirmedFilter}
              onChange={(e) => { setConfirmedFilter(e.target.value); setPage(1); }}
              title="Filter by confirmed status"
            >
              <option value="all">Status: any</option>
              <option value="confirmed">Confirmed</option>
              <option value="unconfirmed">Unconfirmed</option>
            </select>

            <select
              className="h-8 rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={sourceFilter}
              onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
              title="Filter by dimension source"
            >
              <option value="all">Source: any</option>
              <option value="odoo">Odoo</option>
              <option value="fallback">All fallbacks</option>
              <option value="shopify_fallback">Shopify</option>
              <option value="variant_fallback">Variant</option>
              <option value="sc_fallback">Second Chance</option>
              <option value="product_type_brand_avg">Brand + type</option>
              <option value="brand_tag_overlap_avg">Brand + tag</option>
              <option value="product_type_global_avg">Global type</option>
              <option value="global_tag_overlap_avg">Global tag</option>
              <option value="brand_average">Brand avg</option>
              <option value="global_average">Global avg</option>
            </select>

            <select
              className="h-8 rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={fitsPalletFilter}
              onChange={(e) => { setFitsPalletFilter(e.target.value); setPage(1); }}
              title="Filter by pallet fit"
            >
              <option value="all">Pallet fit: any</option>
              <option value="fits">Fits pallet</option>
              <option value="doesnt_fit">No fit</option>
            </select>

            <label className="flex items-center gap-1.5 cursor-pointer select-none whitespace-nowrap">
              <input type="checkbox" checked={heavyFilter} onChange={(e) => { setHeavyFilter(e.target.checked); setPage(1); }} className="h-3.5 w-3.5 rounded border-input accent-orange-500" />
              <span className="flex items-center gap-0.5 text-xs text-muted-foreground"><Package className="h-3 w-3 text-orange-500" />&ge;30kg</span>
            </label>

            <div className="text-xs text-muted-foreground ml-auto whitespace-nowrap">
              <span className="font-medium text-foreground tabular-nums">{totalCount.toLocaleString()}</span> records
            </div>
          </div>
        </div>

        {/* Table */}
        {loading && data.length === 0 ? (
          <div className="flex items-center justify-center h-40">
            <ShippyLoader size={56} message="Loading packaging data..." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table ref={tableRef} className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-2 py-2 w-8 text-center">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 accent-primary cursor-pointer"
                      checked={data.length > 0 && selectedIds.size === data.length}
                      onChange={toggleSelectAll}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  {[
                    { key: 'productId', label: 'Product ID', sort: 'product_id', align: 'left' },
                    { key: 'product', label: 'Product', sort: 'product_name', align: 'left' },
                    { key: 'box', label: 'Box#', sort: 'box_number', align: 'center' },
                    { key: 'dims', label: 'Dimensions (L x W x H)', sort: 'length', align: 'left' },
                    { key: 'weight', label: 'Weight', sort: 'weight', align: 'right' },
                    { key: 'volWeight', label: 'Vol. Weight', sort: 'vol_weight', align: 'right' },
                    { key: 'cost', label: 'Cost', sort: 'unit_cost', align: 'right' },
                    { key: 'attributes', label: 'Attributes', sort: null, align: 'center' },
                  ].map(col => (
                    <th
                      key={col.key}
                      className="px-3 py-2 font-medium relative select-none"
                      style={{ width: `${colPcts[col.key]}%`, minWidth: 40, textAlign: col.align }}
                    >
                      {col.sort ? (
                        <button
                          className={`flex items-center ${col.align === 'right' ? 'justify-end' : col.align === 'center' ? 'justify-center' : ''}`}
                          onClick={() => handleSort(col.sort)}
                        >
                          {col.label} <SortIcon field={col.sort} />
                        </button>
                      ) : col.label}
                      <div
                        className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/30 active:bg-primary/50"
                        onMouseDown={(e) => startResize(col.key, e)}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row) => {
                  const dims = formatDimensions(row);
                  const hasDims = hasDimensions(row);
                  const outlierReasons = outliersFilter ? getOutlierReasons(row) : [];
                  return (
                    <tr key={row.id || row.product_id} className={`border-b border-border hover:bg-muted/30 cursor-pointer ${selectedIds.has(row.id) ? 'bg-primary/5 dark:bg-primary/10' : ''} ${outlierReasons.length > 0 ? 'bg-red-50/50 dark:bg-red-950/10' : (row.dimension_source && row.dimension_source !== 'odoo' && DIM_SOURCE_BADGES[row.dimension_source]) ? DIM_SOURCE_TONE_ROW[DIM_SOURCE_BADGES[row.dimension_source].tone] : ''}`} onClick={() => handleRowClick(row)}>
                      <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 accent-primary cursor-pointer"
                          checked={selectedIds.has(row.id)}
                          onChange={() => toggleSelect(row.id)}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs truncate overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        {row.product_id ? (
                          <a
                            href={`${ODOO_BASE_URL}/web#id=${row.product_id}&model=product.product&view_type=form`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline inline-flex items-center gap-1"
                          >
                            {row.product_id} <ExternalLink className="h-3 w-3 opacity-50" />
                          </a>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-2 overflow-hidden">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-xs truncate" title={row.product_name}>{row.product_name}</p>
                          {row.dimension_source && row.dimension_source !== 'odoo' && DIM_SOURCE_BADGES[row.dimension_source] && (
                            <span
                              className={`inline-flex items-center gap-0.5 flex-shrink-0 rounded px-1 py-0.5 text-[10px] font-medium ${DIM_SOURCE_TONE_BADGE[DIM_SOURCE_BADGES[row.dimension_source].tone]}`}
                              title={DIM_SOURCE_BADGES[row.dimension_source].tooltip}
                            >
                              {DIM_SOURCE_BADGES[row.dimension_source].label}
                            </span>
                          )}
                        </div>
                        {outlierReasons.length > 0 && (
                          <p className="text-[10px] text-red-600 dark:text-red-400 truncate" title={outlierReasons.join(' · ')}>
                            {outlierReasons.join(' · ')}
                          </p>
                        )}
                        {(row.hs_origin || row.hs_code) && (
                          <p className="text-[10px] text-muted-foreground truncate mt-0.5 flex items-center gap-1">
                            {row.hs_origin && (
                              <span title={`Country of origin: ${row.hs_origin}`} className="inline-flex items-center gap-0.5">
                                {row.hs_origin.length === 2 && <span className="text-sm leading-none">{getCountryFlag(row.hs_origin)}</span>}
                                <span className="font-medium">{row.hs_origin}</span>
                              </span>
                            )}
                            {row.hs_origin && row.hs_code && <span className="opacity-40">·</span>}
                            {row.hs_code && (
                              <span title={`HS customs code: ${row.hs_code}`} className="font-mono">
                                HS {row.hs_code}
                              </span>
                            )}
                          </p>
                        )}
                        {(row.barcode || row.vendor_barcode) && (
                          <p className="text-[10px] text-muted-foreground truncate mt-0.5 flex items-center gap-1">
                            {row.barcode && (
                              <span title={`Internal barcode (box ${row.box_number || 1}): ${row.barcode}`} className="font-mono">
                                ▮ {row.barcode}
                              </span>
                            )}
                            {row.barcode && row.vendor_barcode && <span className="opacity-40">·</span>}
                            {row.vendor_barcode && (
                              <span title={`Vendor EAN: ${row.vendor_barcode}`} className="font-mono opacity-75">
                                EAN {row.vendor_barcode}
                              </span>
                            )}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-bold">
                          {row.box_number || 1}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {hasDims ? (
                          <span className={`font-mono text-xs ${outliersFilter && Math.max(row.length, row.width, row.height) > 200 ? 'text-red-600 font-semibold' : ''}`}>{dims} cm</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium">
                            <AlertTriangle className="h-3 w-3" /> No dims
                          </span>
                        )}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono text-xs ${outliersFilter && row.weight > 80 ? 'text-red-600 font-semibold' : ''}`}>
                        {row.weight > 0 ? `${row.weight.toFixed(2)} kg` : '-'}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono text-xs ${outliersFilter && row.vol_weight > 0 && row.weight > 0 && (row.vol_weight / row.weight > 15 || row.weight / row.vol_weight > 8) ? 'text-red-600 font-semibold' : ''}`}>
                        {row.vol_weight > 0 ? `${row.vol_weight.toFixed(2)} kg` : '-'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {row.unit_cost != null && row.unit_cost > 0 ? (
                          `€${row.unit_cost.toFixed(2)}`
                        ) : (
                          <span className="text-red-500/70">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <AttributesCell row={row} />
                      </td>
                    </tr>
                  );
                })}
                {data.length === 0 && !loading && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                      No packaging data found. {searchDebounced ? 'Try a different search term.' : 'Sync from Odoo to get started.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="sticky bottom-0 z-10 flex items-center gap-3 border-t border-border bg-card px-4 py-2.5 shadow-md">
            {(() => {
              const selected = data.filter(r => selectedIds.has(r.id));
              const allFragile = selected.every(r => r.fragile);
              const noneFragile = selected.every(r => !r.fragile);
              const allPallet = selected.every(r => r.shipped_on_pallet);
              const nonePallet = selected.every(r => !r.shipped_on_pallet);
              return (
                <>
                  <span className="text-sm font-medium text-foreground">
                    {selectedIds.size} selected
                  </span>
                  <div className="flex items-center gap-1.5">
                    {!allFragile && (
                      <Button size="sm" variant="outline" onClick={() => handleBulkUpdate({ fragile: true })} disabled={bulkUpdating}>
                        <AlertTriangle className="h-3.5 w-3.5 mr-1.5 text-amber-500" /> Mark Fragile
                      </Button>
                    )}
                    {!noneFragile && (
                      <Button size="sm" variant="outline" onClick={() => handleBulkUpdate({ fragile: false })} disabled={bulkUpdating}>
                        Unmark Fragile
                      </Button>
                    )}
                    {!allPallet && (
                      <Button size="sm" variant="outline" onClick={() => handleBulkUpdate({ shipped_on_pallet: true })} disabled={bulkUpdating}>
                        <Truck className="h-3.5 w-3.5 mr-1.5 text-blue-500" /> Mark Pallet
                      </Button>
                    )}
                    {!nonePallet && (
                      <Button size="sm" variant="outline" onClick={() => handleBulkUpdate({ shipped_on_pallet: false })} disabled={bulkUpdating}>
                        Unmark Pallet
                      </Button>
                    )}
                  </div>
                </>
              );
            })()}
            {bulkUpdating && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <button className="ml-auto text-xs text-muted-foreground hover:text-foreground" onClick={() => setSelectedIds(new Set())}>
              Clear selection
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(1)}
                disabled={page === 1 || loading}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page === 1 || loading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-3 py-1 text-sm font-medium">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                disabled={page === totalPages || loading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages || loading}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Shipping Estimate Modal */}
      <Modal
        open={estimateModalOpen}
        onClose={() => setEstimateModalOpen(false)}
        title={<><Globe className="h-5 w-5 text-primary" /> Shipping Cost Estimates</>}
      >
        {estimateLoading ? (
          <div className="flex items-center justify-center py-12">
            <ShippyLoader size={48} message="Calculating shipping estimates..." />
          </div>
        ) : estimateError ? (
          <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200">
            {estimateError}
          </div>
        ) : estimateData && !estimateData.has_dimensions ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <div>
              <p className="font-medium">Dimensions Missing</p>
              <p className="mt-1">This product does not have valid dimensions. Shipping estimates cannot be calculated.</p>
            </div>
          </div>
        ) : estimateData ? (
          (() => {
            const BADGE_CLS = {
              pallet: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
              heavy: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
              oversize_girth: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
              oversize_longest: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
              volumetric_dominant: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
              direct_likely: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
            };
            const appliedBadges = (estimateData.badges || []).filter(b => b.applies);
            return (
          <div>
            {/* 1. Smarter product header */}
            <div className="mb-4 rounded-md border border-border bg-muted/30 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{estimateData.product.product_name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    ID {estimateData.product.product_id || '-'}
                    {estimateData.product.product_sku ? ` · SKU ${estimateData.product.product_sku}` : ''}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1 shrink-0 justify-end">
                  {appliedBadges.length === 0 ? (
                    <span className="text-[10px] text-muted-foreground italic">no flags</span>
                  ) : appliedBadges.map(b => (
                    <span
                      key={b.key}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${BADGE_CLS[b.key] || 'bg-gray-100 text-gray-700'}`}
                      title={`${b.rule}\nThis product: ${b.product_value}`}
                    >
                      {b.label}
                      <Info className="h-3 w-3 opacity-70" />
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-2.5 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div><span className="text-muted-foreground">Dims:</span> <span className="font-mono">{estimateData.product.length}×{estimateData.product.width}×{estimateData.product.height} cm</span></div>
                <div><span className="text-muted-foreground">Girth:</span> <span className="font-mono">{estimateData.product.girth} cm</span></div>
                <div><span className="text-muted-foreground">Longest:</span> <span className="font-mono">{estimateData.product.longest_side} cm</span></div>
                <div><span className="text-muted-foreground">Sum sides:</span> <span className="font-mono">{estimateData.product.sum_sides} cm</span></div>
                <div className="col-span-2"><span className="text-muted-foreground">Weight:</span> <span className="font-mono">real {estimateData.actual_weight} kg · vol {estimateData.volumetric_weight} kg</span></div>
                <div className="col-span-2"><span className="text-muted-foreground">Billable:</span> <span className="font-mono font-semibold">{estimateData.billable_weight} kg</span> <span className="text-[10px] text-muted-foreground">in vol markets</span></div>
              </div>
            </div>

            {/* 2. Carrier eligibility (product-centric) */}
            {(estimateData.carrier_eligibility?.length > 0) && (
              <div className="mb-4 rounded-md border border-border p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Carrier eligibility</h4>
                  <span className="text-[10px] text-muted-foreground" title="Global surcharge rules. Some countries may have stricter limits.">Global rules · {estimateData.carrier_eligibility.length} carriers</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {estimateData.carrier_eligibility.map(c => {
                    const cls = c.status === 'refused'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      : c.status === 'surcharge'
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                        : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
                    const tooltip = c.status === 'refused'
                      ? `Refused: ${(c.labels || []).join(', ') || 'rule triggered'}\nServes ${c.countries_served} countries`
                      : c.status === 'surcharge'
                        ? `+€${c.surcharge_amount.toFixed(2)} surcharge — ${(c.labels || []).join(', ')}\nServes ${c.countries_served} countries`
                        : `Eligible — no surcharges\nServes ${c.countries_served} countries`;
                    return (
                      <span key={c.carrier_id} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cls}`} title={tooltip}>
                        {c.status === 'refused' ? <XIcon className="h-3 w-3" /> : c.status === 'surcharge' ? <AlertTriangle className="h-3 w-3" /> : <CheckCircle className="h-3 w-3" />}
                        <span className={c.status === 'refused' ? 'line-through' : ''}>{c.carrier_name}</span>
                        {c.status === 'surcharge' && <span className="font-mono">+€{c.surcharge_amount.toFixed(2)}</span>}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 3. Badges explained collapsible */}
            <div className="mb-4 rounded-md border border-border">
              <button
                type="button"
                onClick={() => setBadgesPanelOpen(o => !o)}
                className="w-full px-3 py-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted/30"
              >
                <span className="flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> Badges explained ({appliedBadges.length}/{(estimateData.badges || []).length} applied)</span>
                {badgesPanelOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
              {badgesPanelOpen && (
                <div className="border-t border-border px-3 py-2 space-y-2">
                  {(estimateData.badges || []).map(b => (
                    <div key={b.key} className="flex items-start gap-2 text-xs">
                      <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${b.applies ? (BADGE_CLS[b.key] || 'bg-gray-100 text-gray-700') : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'}`}>
                        {b.label}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-muted-foreground">Rule: <span className="text-foreground">{b.rule}</span></p>
                        <p className="mt-0.5">
                          {b.applies
                            ? <span className="text-emerald-600 dark:text-emerald-400">\u2713 Applies — this product: {String(b.product_value)}</span>
                            : <span className="text-muted-foreground">— Not triggered (this product: {String(b.product_value)})</span>
                          }
                        </p>
                      </div>
                    </div>
                  ))}
                  <p className="mt-2 text-[10px] text-muted-foreground italic">
                    Carrier-specific surcharge labels (oversize, overlength, heavy_parcel\u2026) come from per-carrier rules in admin. Hover any chip in the carrier eligibility section for the exact reason.
                  </p>
                </div>
              )}
            </div>

            {/* 4. Country search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search country..."
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* 5. All countries table — now with Carrier column + surcharges + expandable direct/franco row */}
            {(() => {
              const filtered = estimateData.estimates
                .filter(e => !countrySearch || e.country_name.toLowerCase().includes(countrySearch.toLowerCase()))
                .sort((a, b) => a.country_name.localeCompare(b.country_name));
              const isPallet = estimateData.shipped_on_pallet;
              const baseColCount = isPallet ? 8 : 7;

              return (
                <div className="overflow-x-auto border border-border rounded-md">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b border-border">
                        <th className="px-3 py-2 text-left font-medium">Country</th>
                        <th className="px-3 py-2 text-left font-medium">Carrier</th>
                        <th className="px-3 py-2 text-right font-medium">Weight</th>
                        <th className="px-3 py-2 text-right font-medium">Vol.</th>
                        <th className="px-3 py-2 text-right font-medium">Per-Parcel</th>
                        {isPallet && <th className="px-3 py-2 text-right font-medium">Pallet</th>}
                        <th className="px-3 py-2 text-right font-medium">Final</th>
                        <th className="px-3 py-2 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((est) => {
                        const isOpen = expandedEstCountry === est.country_iso2;
                        const refused = est.surcharge_status === 'refused';
                        return (
                          <React.Fragment key={est.country_iso2}>
                            <tr
                              className={`border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer ${est.uses_row ? 'opacity-60' : ''}`}
                              onClick={() => setExpandedEstCountry(isOpen ? null : est.country_iso2)}
                            >
                              <td className="px-3 py-1.5">
                                <span className="mr-1.5">{getCountryFlag(est.country_iso2)}</span>
                                {est.country_name}
                                {est.uses_row && (
                                  <span className="ml-1.5 inline-flex items-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 px-1.5 py-0.5 text-[10px] font-medium">ROW</span>
                                )}
                                {est.franco_b2c != null && est.franco_b2c > 0 && (
                                  <span className="ml-1.5 inline-flex items-center rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 px-1.5 py-0.5 text-[10px] font-medium" title={`Free B2C shipping above €${est.franco_b2c}`}>
                                    franco €{est.franco_b2c}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-1.5 text-xs">
                                {est.winning_carrier_name ? (
                                  <span className="text-foreground">{est.winning_carrier_name}</span>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className={`px-3 py-1.5 text-right font-mono text-xs ${est.cheapest_method === 'weight_rate' ? 'text-blue-600 dark:text-blue-400 font-semibold' : ''}`}>
                                {est.weight_cost != null ? `€${est.weight_cost.toFixed(2)}` : '-'}
                              </td>
                              <td className={`px-3 py-1.5 text-right font-mono text-xs ${est.cheapest_method === 'volumetric_rate' ? 'text-purple-600 dark:text-purple-400 font-semibold' : ''}`}>
                                {est.vol_cost != null ? `€${est.vol_cost.toFixed(2)}` : '-'}
                              </td>
                              <td className={`px-3 py-1.5 text-right font-mono text-xs ${est.cheapest_method?.includes('coli') ? 'text-teal-600 dark:text-teal-400 font-semibold' : ''}`}>
                                {est.coli_cost != null ? `€${est.coli_cost.toFixed(2)}` : '-'}
                              </td>
                              {isPallet && (
                                <td className={`px-3 py-1.5 text-right font-mono text-xs ${est.cheapest_method === 'pallet' ? 'text-amber-600 dark:text-amber-400 font-semibold' : ''}`}>
                                  {est.pallet_cost != null ? `€${est.pallet_cost.toFixed(2)}` : '-'}
                                </td>
                              )}
                              <td className="px-3 py-1.5 text-right whitespace-nowrap">
                                {refused ? (
                                  <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs font-semibold" title={(est.surcharge_labels || []).join(', ')}>
                                    <XIcon className="h-3 w-3" /> refused
                                  </span>
                                ) : est.cheapest > 0 ? (
                                  <span className="inline-flex items-center gap-1">
                                    <span className="font-mono text-xs font-semibold">{`€${(est.final_cost ?? est.cheapest).toFixed(2)}`}</span>
                                    {est.surcharge_amount > 0 && (
                                      <span className="text-[10px] text-amber-700 dark:text-amber-400" title={(est.surcharge_labels || []).join(', ')}>
                                        +€{est.surcharge_amount.toFixed(2)}
                                      </span>
                                    )}
                                    <MethodBadge method={est.cheapest_method} />
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-muted-foreground">
                                {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                              </td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-muted/20 border-b border-border">
                                <td colSpan={baseColCount} className="px-4 py-2 text-xs">
                                  <div className="flex flex-wrap items-start gap-x-6 gap-y-1">
                                    {est.direct && (
                                      <span>
                                        <span className="text-muted-foreground">Direct shipping:</span>{' '}
                                        <span className="font-mono font-semibold">€{est.direct.cost.toFixed(2)}</span>
                                        {est.direct.carrier_name && <span className="text-muted-foreground"> via {est.direct.carrier_name}</span>}
                                      </span>
                                    )}
                                    {est.franco_b2b != null && est.franco_b2b > 0 && (
                                      <span>
                                        <span className="text-muted-foreground">B2B franco:</span>{' '}
                                        <span className="font-mono">€{est.franco_b2b}</span>
                                      </span>
                                    )}
                                    {est.surcharge_labels?.length > 0 && (
                                      <span>
                                        <span className="text-muted-foreground">Surcharges:</span>{' '}
                                        <span className="text-amber-700 dark:text-amber-400">{est.surcharge_labels.join(', ')}</span>
                                      </span>
                                    )}
                                    {!est.direct && (est.franco_b2b == null || est.franco_b2b <= 0) && !est.surcharge_labels?.length && (
                                      <span className="text-muted-foreground italic">No additional details for this market.</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                      {filtered.length === 0 && (
                        <tr>
                          <td colSpan={baseColCount} className="px-3 py-6 text-center text-muted-foreground text-sm">
                            No countries match "{countrySearch}"
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
            );
          })()
        ) : null}
      </Modal>

      </>
      )}

      {/* Sync Progress Modal */}
      <Modal
        open={syncModalOpen}
        onClose={syncing ? undefined : () => setSyncModalOpen(false)}
        title={<><Database className="h-5 w-5 text-primary" /> Sync from Odoo</>}
      >
        {syncing ? (
          <div className="flex flex-col items-center py-8 gap-4">
            <ShippySpinner size={48} />
            <div className="text-center">
              <p className="text-sm font-medium text-foreground">Syncing packaging data from Odoo...</p>
              <p className="text-xs text-muted-foreground mt-1">
                This usually takes 30–40 seconds
              </p>
              <p className="text-2xl font-mono font-semibold text-primary mt-3">
                {Math.floor(syncElapsed / 60).toString().padStart(2, '0')}:{(syncElapsed % 60).toString().padStart(2, '0')}
              </p>
            </div>
          </div>
        ) : syncResult?.success ? (
          <div className="flex flex-col items-center py-6 gap-4">
            <CheckCircle className="h-12 w-12 text-emerald-500" />
            <p className="text-sm font-medium text-foreground">{syncResult.message}</p>
            {syncResult.stats && (
              <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm mt-2">
                <span className="text-muted-foreground">Products processed</span>
                <span className="font-mono font-medium text-right">{syncResult.stats.products_processed?.toLocaleString()}</span>
                <span className="text-muted-foreground">Packaging records</span>
                <span className="font-mono font-medium text-right">{syncResult.stats.total_rows?.toLocaleString()}</span>
                <span className="text-muted-foreground">With Shopify IDs</span>
                <span className="font-mono font-medium text-right">{syncResult.stats.with_shopify_ids?.toLocaleString()}</span>
                <span className="text-muted-foreground">Duration</span>
                <span className="font-mono font-medium text-right">{syncResult.stats.duration_seconds}s</span>
              </div>
            )}
            <Button className="mt-4" onClick={() => setSyncModalOpen(false)}>Close</Button>
          </div>
        ) : syncResult?.error ? (
          <div className="flex flex-col items-center py-6 gap-4">
            <AlertTriangle className="h-12 w-12 text-red-500" />
            <p className="text-sm font-medium text-red-600 dark:text-red-400">{syncResult.message}</p>
            <Button variant="outline" className="mt-4" onClick={() => setSyncModalOpen(false)}>Close</Button>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default PackagingData;
